import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
  Background,
  BackgroundVariant,
  MiniMap,
  NodeResizer,
  NodeResizeControl,
  ConnectionMode,
  SelectionMode,
  useReactFlow,
  useUpdateNodeInternals,
  useViewport,
  type Node,
  type Edge,
  type NodeProps,
  type EdgeProps,
  type NodeChange,
  type Viewport,
} from "@xyflow/react";
import {
  Hand,
  MousePointer2,
  Maximize2,
  Plus,
  Minus,
  Network,
  CircleDot,
  Pin,
  PinOff,
  X,
  Check,
  ChevronDown,
  GitBranch,
  KeyRound,
  Table2,
  MoreHorizontal,
  CornerDownRight,
  RotateCcw,
  Info,
  AlertTriangle,
  Eye,
  Map as MapIcon,
  MapPinOff,
  Layers,
  Move,
  HelpCircle,
  Diamond,
  Grip,
  LockKeyhole,
  Palette,
  AlignHorizontalJustifyStart,
  AlignHorizontalJustifyCenter,
  AlignHorizontalJustifyEnd,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignHorizontalSpaceAround,
  AlignVerticalSpaceAround,
  SlidersHorizontal,
  Grid3X3,
  ListTree,
  TreePine,
  Filter,
  Unlock,
  Bookmark,
  Copy,
  Download,
} from "lucide-react";
import type {
  ModelDocument,
  Preferences,
  Operation,
  DomainObject,
  Connector,
  Relationship,
  Notation,
  Point,
  Diagram,
  Side,
  EntityDetailLevel,
  RelationshipStyle,
  LayoutCheckpoint,
} from "../../shared/contracts.js";
import { diagramContextMenuFontSize, diagramContextMenuPosition, diagramContextMenuSpace } from "./diagramContextMenu.js";
import {
  SIDES,
  normal,
  pointOnSide,
  nearestAttachment,
  boxesFromAppearances,
  routeRelationships,
  routeEditWaypoints,
  manualConnectorChanges,
  connectorsForAutoLayout,
  pathToSvg,
  pathMidpoint,
  relationshipLabelAnchor,
  layoutRelationshipLabel,
  repairConnectorRoute,
  connectorUpdatesForMovedBoxes,
  arrangeBoxes,
  type Alignment,
  createLayout,
  geometryFingerprint,
  distance,
  insertWaypoint,
  countRouteCrossings,
  layoutOperations,
  type CanvasBox,
  type RoutedRelationship,
  type LayoutOptions,
  type LayoutCandidate,
} from "./canvasGeometry.js";
import {
  detailLevels, detailLevelOptions, effectiveDetailLevel, visibleEntityFields, getEntityColors,
  resolveRelationshipStyle, effectiveEntityDisplay, relationshipDashArray, type AppearanceTarget,
} from "../../shared/appearance.js";
import { diagramSelectionIds, isTextEditingTarget, withMovedConnectorUpdates, createRouteDragSession, createEdgeDragController, dragBounds, clampDragPosition, type DragBounds } from "./canvasInteraction.js";
import { resolveModelTypePreferences, fitModelEntitiesToText, lockModelEntitySizes, canvasDetailLevel, entityFields, entitySize, resolveEntityContent, keyMemberLabels, fieldType, vocabulary, type EntityContent, type EntityDimensions } from "../../shared/entityPresentation";
import { WorkspaceCommand, DockableToolbar, DockSlot, useToolbarDragging } from "./WorkspaceTools";
import EntityPreview from "./EntityPreview";
import { EntityDisplayControls } from "./AppearanceControls";
import RelationshipIcon from "./RelationshipIcon";
import { SavedLayoutsPanel } from "./SavedLayoutsPanel";
import { captureLayoutCheckpoint, checkpointSaveOperations, checkpointDeleteOperations, previewLayoutCheckpoint } from "./layoutCheckpoints";
import { buildDiagramSvg, copyDiagramExport, downloadDiagramExport, diagramPngSize } from "./diagramExport";
import "@xyflow/react/dist/style.css";
import "./canvas.css";
import { compactBoxes, autoFitDiagram, diagramGeometryOperations } from "./canvasCommands";
import { fittedViewport, findEntityOverlaps, visibleEntityPosition } from "./canvasPlacement";

export interface ModelCanvasProps {
  document: ModelDocument;
  diagramId: string;
  preferences: Preferences;
  selection: string[];
  onSelection: (ids: string[]) => void;
  onOperations: (ops: Operation[], summary: string) => void;
  onOpenObject: (id: string) => void;
  onOpenRelationship: (id: string) => void;
  onHelp: (id: string) => void;
  hideNotationControl?: boolean;
  hideLayoutControl?: boolean;
  readOnly?: boolean;
  canEditModel?: boolean;
  onPreferencesChange?: (patch: Partial<Preferences>) => void;
  onOpenAppearance?: (target: AppearanceTarget) => void;
  onAddAttribute?: (objectId: string, afterFieldId?: string) => void;
  onReorderField?: (fieldId: string, direction: -1 | 1) => void;
  relationshipSource?: string | null;
  relationshipPicking?: boolean;
  onCancelRelationship?: () => void;
  interactionBlocked?: boolean;
  onBeginRelationship?: (sourceId: string) => void;
  onRelationshipTarget?: (targetId: string) => void;
  lockedPreferences?: string[];
  onAddEntity?: (position?: Point) => void;
  onViewportPlacement?: (resolver: ((size: EntityDimensions, preferred?: Point) => Point) | null) => void;
  onGestureActiveChange?: (active: boolean) => void;
  onCreateSubjectArea?: (selectedIds: string[]) => void;
  onCreateView?: (selectedIds: string[]) => void;
  onAddEntities?: () => void;
  onImport?: () => void;
  onBuildingBlock?: () => void;
  command?: {
    id: number;
    type:
      | "fit"
      | "auto-fit"
      | "compact"
      | "layout-hierarchical"
      | "layout-clustered"
      | "zoom-in"
      | "zoom-out"
      | "select-all";
  };
}
type EntityData = Record<string, unknown> & {
  object: DomainObject;
  content: EntityContent;
  fields: DomainObject[];
  keys: DomainObject[];
  preferences: Preferences;
  conceptual: boolean;
  detailLevel: EntityDetailLevel;
  totalFields: number;
  colors: ReturnType<typeof getEntityColors>;
  displayWidth: number;
  displayHeight?: number;
  resizable: boolean;
  sizeLocked: boolean;
  onPreview: (objectId: string, x: number, y: number) => void;
  onHover: (objectId: string, x: number, y: number) => void;
  onLeave: () => void;
  onFieldMenu: (fieldId: string, x: number, y: number) => void;
  onResizeStart: (id: string) => void;
  onResize: (id: string, bounds: Point & { width: number; height: number }, complete: boolean) => void;
  notation: Notation;
  degree: number;
  pinned: boolean;
  collapsed: boolean;
  moveTool: boolean;
  duplicate: boolean;
  dependent: boolean;
  selectedFieldIds: string[];
  foreignIds: Set<string>;
  onField: (id: string) => void;
  onOpen: (id: string) => void;
  onMeasure: (id: string, width: number, height: number) => void;
  onMenu: (id: string, x: number, y: number) => void;
};
type EntityNode = Node<EntityData, "entity">;
type RelationData = Record<string, unknown> & {
  route: RoutedRelationship;
  boxes?: CanvasBox[];
  notation: Notation;
  style: ReturnType<typeof resolveRelationshipStyle>;
  preferences: Preferences;
  connector?: Connector;
  readOnly: boolean;
  editVersion: string;
  onWaypoint: (
    id: string,
    index: number,
    point: Point,
    complete: boolean,
  ) => void;
  onRouteEdit: (id: string, route: RoutedRelationship, kind: "segment" | "bend", index: number, delta: Point, complete: boolean) => void;
  onEndpoint: (
    id: string,
    end: "source" | "target",
    point: Point,
    complete: boolean,
  ) => void;
  onRouteMenu: (
    id: string,
    x: number,
    y: number,
    point: Point,
    waypoint?: number,
  ) => void;
  onCancel: () => void;
  onGestureActiveChange?: (active: boolean) => void;
};
type RelationEdge = Edge<RelationData, "relationship">;
const positions: Record<Side, Position> = {
  top: Position.Top,
  right: Position.Right,
  bottom: Position.Bottom,
  left: Position.Left,
};
const notationNames: Record<Notation, string> = {
  "crow-foot": "Crow's foot",
  idef1x: "IDEF1X",
  uml: "UML association",
  chen: "Chen conceptual",
};
const cameras = new Map<string, Viewport>();
const geometryCameras = new Map<string, Viewport>();
const layoutModes: { id: LayoutOptions["mode"]; label: string; description: string }[] = [
  { id: "hierarchical", label: "Hierarchy", description: "Layers by dependency" },
  { id: "clustered", label: "Related circles", description: "Groups and shared bridges" },
  { id: "tree", label: "Tree", description: "Parent and child branches" },
  { id: "circular", label: "Circular", description: "Connected groups around circles" },
  { id: "orthogonal", label: "Orthogonal", description: "Layers with right-angle routes" },
  { id: "symmetric", label: "Symmetric", description: "Balanced connected structures" },
];

const EntityCard = memo(function EntityCard({ id, data, selected }: NodeProps<EntityNode>) {
  const ref = useRef<HTMLDivElement>(null), internals = useUpdateNodeInternals();
  const [clippedRows,setClippedRows]=useState(0);
  const { object, preferences, conceptual, content } = data;
  const { visibleFields: fields, keys, visibleIdentifiers, description, showDescription, hiddenCount, hasMore, terms } = content;
  const nameOnly = !content.fieldsRequested && !content.identifierRequested && !showDescription;
  const preview = () => { const rect=ref.current?.getBoundingClientRect(); if(rect)data.onPreview(object.objectId,rect.right,rect.top); };
  const showPreview = (event: React.MouseEvent<HTMLButtonElement>) => { event.stopPropagation();event.currentTarget.focus();preview(); };
  useEffect(() => { const el=ref.current; if(!el)return; const observer=new ResizeObserver(()=>{data.onMeasure(id,el.offsetWidth,el.offsetHeight);internals(id);});observer.observe(el);return()=>observer.disconnect(); },[id,data.onMeasure,internals]);
  useEffect(()=>{internals(id);},[id,data.displayWidth,data.displayHeight,data.degree,internals]);
  useEffect(()=>{const element=ref.current?.querySelector<HTMLElement>('.dg-canvas-fields');if(!element){setClippedRows(0);return;}const measure=()=>{const bounds=element.getBoundingClientRect();const rows=[...element.querySelectorAll<HTMLElement>('.dg-canvas-field')];setClippedRows(rows.filter(row=>{const rect=row.getBoundingClientRect();return rect.top<bounds.top-1||rect.bottom>bounds.bottom+1;}).length);};const observer=new ResizeObserver(measure);observer.observe(element);element.addEventListener('scroll',measure);measure();return()=>{observer.disconnect();element.removeEventListener('scroll',measure);};},[fields.length,data.displayHeight,data.displayWidth,preferences.fontSize,content.fieldsRequested]);
  const showMore=hasMore||clippedRows>0;
  const moreLabel=conceptual?'More details':`${hiddenCount+clippedRows} more ${terms.fields}`;
  return <div ref={ref} className={`nopan dg-canvas-entity ${preferences.autoSizeEntities && !data.sizeLocked ? "is-auto-fit" : ""} ${selected?'is-selected':''} ${conceptual?'is-conceptual':''} ${nameOnly&&preferences.centerEntityNames?'is-name-centered':''} ${conceptual&&preferences.centerEntityNames?'is-concept-centered':''} ${data.notation === 'idef1x'&&data.dependent?'is-dependent':''}`}
    onPointerEnter={e=>{const rect=e.currentTarget.getBoundingClientRect();data.onHover(object.objectId,rect.right,rect.top);}} onPointerLeave={data.onLeave}
    onKeyDown={event=>{if(event.altKey&&event.key==='Enter'){event.preventDefault();event.stopPropagation();preview();}}}
    style={{'--dg-entity-size':`${preferences.fontSize}px`,'--dg-title-size':`${preferences.titleSize}px`,fontFamily:preferences.fontFamily,width:data.displayWidth,height:data.displayHeight,'--dg-entity-surface':data.colors.background,'--dg-entity-header':data.colors.header,'--dg-entity-border':data.colors.border,'--dg-entity-accent':data.colors.accent,'--dg-entity-text':data.colors.text,'--dg-entity-header-text':data.colors.headerText,'--dg-entity-muted':data.colors.muted} as CSSProperties}>
    <NodeResizer isVisible={!!selected&&data.resizable} minWidth={240} minHeight={content.fieldsRequested?150:96} handleClassName="dg-canvas-resize-handle" lineClassName="dg-canvas-resize-line" onResizeStart={()=>data.onResizeStart(id)} onResize={(_,bounds)=>data.onResize(id,bounds,false)} onResizeEnd={(_,bounds)=>data.onResize(id,bounds,true)}/>
    {!!selected && data.resizable && SIDES.map(side=><NodeResizeControl key={`resize-${side}`} position={side} resizeDirection={side==='top'||side==='bottom'?'vertical':'horizontal'} minWidth={240} minHeight={content.fieldsRequested?150:96} className="dg-canvas-resize-handle dg-canvas-side-resize" onResizeStart={()=>data.onResizeStart(id)} onResize={(_,bounds)=>data.onResize(id,bounds,false)} onResizeEnd={(_,bounds)=>data.onResize(id,bounds,true)}><span aria-label={`Resize ${object.name} from ${side}`} title={`Resize from ${side}`} /></NodeResizeControl>)}
    {SIDES.flatMap(side=>Array.from({length:Math.max(1,data.degree)},(_,slot)=>{const span=side==='left'||side==='right'?(data.displayHeight??160):data.displayWidth;const inset=Math.min(22,span/4),fraction=data.degree<=1?.5:(slot+1)/(data.degree+1),along=inset+fraction*(span-inset*2);return <Handle key={`${side}:${slot}`} id={`${side}:${slot}`} type="source" position={positions[side]} isConnectable={false} className="dg-canvas-port" style={side==='top'||side==='bottom'?{left:along}:{top:along}} aria-label={`${object.name}, ${side} attachment ${slot+1} of ${Math.max(1,data.degree)}`}/>;}))}
    <div className="dg-canvas-entity-content">
      <div className="dg-canvas-entity-heading"><div className="dg-canvas-entity-kind"><span>{conceptual?<Diamond size={12}/>:<Table2 size={12}/>} {terms.object.toUpperCase()}</span><span>{data.pinned&&<Pin size={12} aria-label="Position pinned"><title>Position pinned</title></Pin>}{data.sizeLocked&&<LockKeyhole size={12} aria-label="Size locked"><title>Size locked</title></LockKeyhole>}{data.duplicate&&<Layers size={12} aria-label="Additional appearance"/>}{showMore&&<button className="nodrag nopan entity-filter-cue" aria-label={`${moreLabel} in ${object.name}`} title={`${moreLabel}. ${data.detailLevel} view. Open a temporary full preview.`} onPointerDown={event=>event.stopPropagation()} onClick={showPreview}><Filter size={12}/></button>}</span></div>
      <div className="dg-canvas-entity-title"><span title={object.displayName||object.name}>{object.displayName||object.name}</span><button className="nodrag nopan dg-canvas-entity-more" aria-label={`Actions for ${object.name}`} onClick={e=>{e.stopPropagation();data.onMenu(id,e.clientX,e.clientY);}}><MoreHorizontal size={16}/></button></div>
      {object.schema&&terms.physical&&<div className="dg-canvas-namespace" title={object.schema}>{object.schema}</div>}
      </div>
      {showDescription&&<p className={`entity-brief-description nopan nowheel ${data.moveTool ? "" : "nodrag"}`} tabIndex={0}>{description}</p>}
      {conceptual ? (content.identifierRequested&&<div className={`dg-canvas-concept-body nopan nowheel ${data.moveTool ? "" : "nodrag"}`}><div className="dg-canvas-identifiers">{visibleIdentifiers.length?visibleIdentifiers.map(key=><button className={`nopan ${data.moveTool ? "" : "nodrag"}`} key={key.objectId} onClick={e=>{e.stopPropagation();data.onField(key.objectId);}} title={[key.description, ...(key.members??[]).map((id,index)=>`${index+1}. ${content.fields.find(f=>f.objectId===id)?.name??'Member unavailable'}`)].filter(Boolean).join('\n')}><KeyRound size={12}/><span>{key.displayName||key.name}</span></button>):<p>{content.identifierLabel} not defined</p>}</div></div>) : content.fieldsRequested&&<div className="dg-canvas-fields nowheel" tabIndex={0} aria-label={`${terms.fields} of ${object.name}`}>
        {content.keyRowOverrun>0&&<div className="dg-canvas-cap-note" role="status">{content.keyRowOverrun} key rows above the {preferences.attributeDisplayLimit}-row limit.</div>}
        {content.primaryConflict&&<div className="dg-canvas-no-fields" role="status">Multiple primary selections; review Properties.</div>}
        {fields.length?fields.map(field=>{const roles=keyMemberLabels(field.objectId,keys),isPrimary=content.primaryKeys.some(k=>k.members?.includes(field.objectId));return <button key={field.objectId} className={`nopan ${data.moveTool ? "" : "nodrag"} dg-canvas-field ${isPrimary?'is-primary':''} ${data.selectedFieldIds.includes(field.objectId)?'is-field-selected':''}`} style={{gridTemplateColumns:`minmax(28px,max-content) minmax(0,1fr)${content.showDataType?' minmax(35px,max-content)':''}${content.showNullability?' minmax(40px,max-content)':''}`}} onClick={e=>{e.stopPropagation();data.onField(field.objectId);}} onDoubleClick={e=>{e.stopPropagation();data.onOpen(field.objectId);}} onContextMenu={e=>{e.preventDefault();e.stopPropagation();data.onFieldMenu(field.objectId,e.clientX,e.clientY);}} title={`${field.name}${field.description?`: ${field.description}`:''}${roles.length?'\n'+roles.map(r=>r.label).join('\n'):''}`}>
        <span className="dg-canvas-key-role">{roles.map(role=><small key={role.label} title={role.label} aria-label={role.label}>{role.short}{role.ordinal}</small>)}{!roles.length&&field.unique&&<small title="Unique field">U</small>}{data.foreignIds.has(field.objectId)&&<CornerDownRight size={11} aria-label="Foreign key member"/>}</span>
        <span className="dg-canvas-field-name">{field.displayName||field.name}</span>{content.showDataType&&<span className="dg-canvas-field-type">{fieldType(field,terms.physical)}</span>}{content.showNullability&&<span className="dg-canvas-field-null">{isPrimary||field.nullable===false?'NOT NULL':field.nullable===true?'NULL':'Not specified'}</span>}
      </button>;}):<div className="dg-canvas-no-fields">{data.totalFields?`No ${terms.fields} match this detail level`:`No ${terms.fields} yet`}</div>}</div>}
      {showMore?<button className="nodrag nopan entity-more-fields" aria-label={`Show all details for ${object.name}`} onPointerDown={event=>event.stopPropagation()} onClick={showPreview}><Plus size={12}/>{moreLabel}</button>:!nameOnly&&<div className="dg-canvas-entity-footer"><span>{conceptual?'Business details':`${data.totalFields} ${terms.fields}`}</span><button className="nodrag nopan" aria-label={`Preview ${terms.object} ${object.name}`} title="Open complete details; Alt+Enter also opens preview" onPointerDown={event=>event.stopPropagation()} onClick={showPreview}><Eye size={12}/> Preview</button></div>}
    </div>
  </div>;
});

function EndpointMark({
  point,
  side,
  many,
  optional,
  notation,
  identifying,
}: {
  point: Point;
  side: Side;
  many: boolean;
  optional: boolean;
  notation: Notation;
  identifying: boolean;
}) {
  const n = normal(side),
    angle = (Math.atan2(n.y, n.x) * 180) / Math.PI;
  if (notation === "uml" || notation === "chen")
    return (
      <text
        x={point.x + n.x * 19 + (n.y ? 9 : 0)}
        y={point.y + n.y * 19 - (n.x ? 7 : 0)}
        className="dg-canvas-multiplicity"
        textAnchor={n.x < 0 ? "end" : "start"}
      >
        {notation === "uml"
          ? `${optional ? "0" : "1"}..${many ? "*" : "1"}`
          : many
            ? "N"
            : "1"}
      </text>
    );
  if (notation === "idef1x")
    return (
      <g
        transform={`translate(${point.x} ${point.y}) rotate(${angle})`}
        className="dg-canvas-endmark"
      >
        {many ? (
          <circle cx={8} cy={0} r={4} fill="currentColor" />
        ) : (
          <path d="M 8 -5 L 8 5" />
        )}
        {optional && (
          <path
            d="M 17 0 L 22 -4 L 27 0 L 22 4 Z"
            fill="var(--canvas, #f5f7f8)"
          />
        )}
        {identifying && !many && <path d="M 13 -5 L 13 5" />}
      </g>
    );
  return (
    <g
      transform={`translate(${point.x} ${point.y}) rotate(${angle})`}
      className="dg-canvas-endmark"
    >
      {many ? (
        <path d="M 1 -6 L 13 0 L 1 6 M 1 0 L 13 0" />
      ) : (
        <path d="M 8 -6 L 8 6" />
      )}
      {optional ? (
        <circle cx={21} cy={0} r={4} fill="var(--canvas, #f5f7f8)" />
      ) : (
        <path d="M 20 -6 L 20 6" />
      )}
    </g>
  );
}

const RelationshipEdge = memo(function RelationshipEdge({
  id,
  data,
  selected,
}: EdgeProps<RelationEdge>) {
  const flow = useReactFlow<EntityNode, RelationEdge>();
  const { zoom: routeZoom } = useViewport();
  const activeGesture = useRef<(() => void) | null>(null);
  useEffect(() => () => activeGesture.current?.(), []);
  useEffect(() => { activeGesture.current?.(); }, [data?.readOnly, data?.editVersion]);
  if (!data) return null;
  const { route, notation, connector } = data,
    r = route.relationship,
    middle = pathMidpoint(route.points),
    caption = data.style.label.source === "description" && r.description?.trim() ? r.description : r.name,
    labelAnchor = layoutRelationshipLabel(route.points, data.style.label, caption, data.preferences.fontFamily, 11, data.boxes),
    line = pathToSvg(route.points);
  const gripRadius = (pixels: number) => pixels / Math.max(.12, routeZoom);
  const drag = (
    event: ReactPointerEvent<SVGElement>,
    kind: "source" | "target" | number | { kind: "segment" | "bend"; index: number },
  ) => {
    if (data.readOnly || event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    activeGesture.current?.();
    const element = event.currentTarget, pointerId = event.pointerId;
    const start = flow.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const baselineRoute = route;
    element.setPointerCapture(pointerId);
    data.onGestureActiveChange?.(true);
    const session = createRouteDragSession(pointerId, start, routeZoom, (point, delta, complete) => {
      if (typeof kind === "object") data.onRouteEdit(id, baselineRoute, kind.kind, kind.index, delta, complete);
      else if (typeof kind === "number") data.onWaypoint(id, kind, point, complete);
      else data.onEndpoint(id, kind, point, complete);
    }, data.onCancel);
    const flowPoint = (e: PointerEvent) => flow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const move = (e: PointerEvent) => { session.move(e.pointerId, flowPoint(e)); };
    const end = (e: PointerEvent) => { if (session.finish(e.pointerId, flowPoint(e))) cleanup(); };
    const cancel = () => { session.cancel(); cleanup(); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); cancel(); } };
    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("blur", cancel);
      window.removeEventListener("keydown", key, true);
      if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
      activeGesture.current = null;
      data.onGestureActiveChange?.(false);
    };
    activeGesture.current = cancel;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("blur", cancel);
    window.addEventListener("keydown", key, true);
  };
  return (
    <g
      className={`nopan dg-canvas-relationship ${selected ? "is-selected" : ""} ${route.warnings.length ? "has-warning" : ""}`}
      style={{ "--dg-relationship-color": data.style.color, "--dg-relationship-width": data.style.width, fontFamily: data.preferences.fontFamily } as CSSProperties}
    >
      <path
        d={line}
        fill="none"
        stroke="transparent"
        strokeWidth={18}
        vectorEffect="non-scaling-stroke"
        className="nopan react-flow__edge-interaction"
      />
      {selected && <path d={line} className="dg-canvas-edge-halo" />}
      <path
        d={line}
        className="dg-canvas-edge-line"
        strokeWidth={data.style.width}
        strokeDasharray={
          relationshipDashArray(data.style, notation, r.identifying)
        }
      />
      {selected && !data.readOnly && route.points.slice(0, -1).map((point, index) => {
        const next = route.points[index + 1];
        return <path key={`segment-${index}`} data-route-segment={index} aria-label={`Drag relationship segment ${index + 1}`} d={`M ${point.x} ${point.y} L ${next.x} ${next.y}`} fill="none" stroke="transparent" strokeWidth={16} vectorEffect="non-scaling-stroke" className="nopan nodrag dg-canvas-route-segment" onPointerDown={event => drag(event, { kind: "segment", index })}><title>Drag this segment in any direction; Escape cancels</title></path>;
      })}
      <EndpointMark
        point={route.sourceAttachment.point}
        side={route.sourceAttachment.side}
        many={r.sourceCardinality === "many"}
        optional={r.sourceOptional}
        notation={notation}
        identifying={r.identifying}
      />
      <EndpointMark
        point={route.targetAttachment.point}
        side={route.targetAttachment.side}
        many={r.targetCardinality === "many"}
        optional={r.targetOptional}
        notation={notation}
        identifying={r.identifying}
      />
      {notation === "chen" && (
        <path
          transform={`translate(${middle.x} ${middle.y})`}
          d="M -44 0 L 0 -18 L 44 0 L 0 18 Z"
          className="dg-canvas-chen-diamond"
        />
      )}
      {labelAnchor.fallback && data.preferences.showRelationshipNames !== false && data.style.label.visible !== false && <path d={`M ${labelAnchor.routePoint.x} ${labelAnchor.routePoint.y} L ${labelAnchor.x} ${labelAnchor.y}`} stroke={data.style.color} strokeDasharray="2 3" fill="none"/>}
      {data.preferences.showRelationshipNames !== false && data.style.label.visible !== false && r.name && (
        <g
          className="dg-canvas-relationship-label"
          data-orientation={data.style.label.orientation}
          data-placement={data.style.label.placement}
          data-anchor={data.style.label.position}
          transform={`translate(${labelAnchor.x} ${labelAnchor.y}) rotate(${labelAnchor.angle})`}
        >
          <title>{caption}</title>
          {(data.style.label.placement === "on-line" || labelAnchor.fallback) && (
            <rect
              x={-labelAnchor.width/2} y={-labelAnchor.height/2}
              width={labelAnchor.width} height={labelAnchor.height} rx={5}
              className="dg-canvas-label-bg"
            />
          )}
          <text textAnchor="middle" dominantBaseline="central" className="dg-canvas-relation-label">
            {labelAnchor.lines.map((line,index)=><tspan key={index} x={0} y={(index-(labelAnchor.lines.length-1)/2)*16}>{line}</tspan>)}
          </text>
        </g>
      )}
      {selected && !data.readOnly && (
        <>
          {route.points.slice(1, -1).map((point, index) => <circle key={`bend-${index + 1}`} data-route-bend={index + 1} aria-label={`Drag relationship bend ${index + 1}`} cx={point.x} cy={point.y} r={gripRadius(4)} className="nopan nodrag dg-canvas-route-bend" onPointerDown={event => drag(event, { kind: "bend", index: index + 1 })}><title>Drag bend {index + 1} in any direction; Escape cancels</title></circle>)}
          {[
            ["source", route.sourceAttachment.point],
            ["target", route.targetAttachment.point],
          ].map(([kind, p]) => {
            const point = p as Point;
            return (
              <circle
                key={kind as string}
                cx={point.x}
                cy={point.y}
                r={gripRadius(6)}
                className="nodrag nopan dg-canvas-endpoint-grip"
                onPointerDown={(e) => drag(e, kind as "source" | "target")}
              >
                <title>
                  Drag {kind as string} attachment to another side of the same
                  entity
                </title>
              </circle>
            );
          })}
          {(connector?.waypoints ?? []).map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={gripRadius(5)}
              className="nodrag nopan dg-canvas-waypoint"
              onPointerDown={(e) => drag(e, i)}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                data.onRouteMenu(id, e.clientX, e.clientY, p, i);
              }}
            >
              <title>Drag waypoint {i + 1}; right-click to remove</title>
            </circle>
          ))}
        </>
      )}
    </g>
  );
});
const nodeTypes = { entity: EntityCard },
  edgeTypes = { relationship: RelationshipEdge };
interface ContextMenu {
  kind: "node" | "edge" | "canvas" | "selection";
  id?: string;
  x: number;
  y: number;
  point: Point;
  waypoint?: number;
  fieldId?: string;
}
interface Preview {
  candidate: LayoutCandidate;
  options: LayoutOptions;
  base: string;
  settings: string;
  viewport: Viewport;
  checkpoint?: { id: string; name: string; operations: Operation[]; connectors: Connector[]; added: number; removed: number; pinned: number; adjusted: number };
}

function Canvas({
  document,
  diagramId,
  preferences: userPreferences,
  selection,
  onSelection,
  onOperations,
  onOpenObject,
  onOpenRelationship,
  onHelp,
  readOnly = false,
  canEditModel = !readOnly,
  onPreferencesChange,
  onOpenAppearance,
  onAddAttribute,
  onReorderField,
  relationshipSource,
  relationshipPicking = false,
  onCancelRelationship,
  interactionBlocked = false,
  onBeginRelationship,
  onRelationshipTarget,
  lockedPreferences = [],
  onAddEntity,
  onViewportPlacement,
  onGestureActiveChange,
  onCreateSubjectArea,
  onCreateView,
  onAddEntities,
  onImport,
  onBuildingBlock,
  hideNotationControl = false,
  hideLayoutControl = false,
  command,
}: ModelCanvasProps) {
  const toolbarDragging = useToolbarDragging();
  const flow = useReactFlow<EntityNode, RelationEdge>(),
    container = useRef<HTMLDivElement>(null);
  const contextMenuElement = useRef<HTMLDivElement>(null);
  const { zoom: menuZoom } = useViewport();
  const contextMenuFontSize = diagramContextMenuFontSize(menuZoom);
  const diagram =
    document.diagrams.find((d) => d.diagramId === diagramId) ??
    document.diagrams[0];
  const preferences = useMemo(() => resolveModelTypePreferences(userPreferences, document, diagram), [userPreferences, document, diagram]);
  const [entityPreview, setEntityPreview] = useState<{objectId:string;x:number;y:number;mode:'hover'|'click'}|null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout>|undefined>(undefined), closeTimer = useRef<ReturnType<typeof setTimeout>|undefined>(undefined);
  const hovered = useRef<string|null>(null);
  const hoverBlocked = useRef(false);
  const localSizes = useRef(new Map<string,{dimensions:EntityDimensions;stored:string;detail:string;text:string}>());
  const closeEntityPreview = useCallback(() => { clearTimeout(hoverTimer.current); clearTimeout(closeTimer.current); setEntityPreview(null); }, []);
  const keepEntityPreview = useCallback(() => { clearTimeout(closeTimer.current); }, []);
  const leaveEntity = useCallback(() => { hovered.current=null; clearTimeout(hoverTimer.current); closeTimer.current=setTimeout(()=>setEntityPreview(old=>old?.mode==='click'?old:null),250); }, []);
  useEffect(() => () => { clearTimeout(hoverTimer.current); clearTimeout(closeTimer.current); }, []);
  useEffect(() => { closeEntityPreview(); }, [diagramId, diagram?.representationId, diagram?.perspective, document.model.modelId, closeEntityPreview]);
  useEffect(() => { if (entityPreview && !document.objects.some(o=>o.objectId===entityPreview.objectId)) closeEntityPreview(); }, [document.objects,entityPreview,closeEntityPreview]);
  const [sizes, setSizes] = useState<
    Record<string, { width: number; height: number }>
  >({});
  const [resizeDrafts, setResizeDrafts] = useState<Record<string, { width: number; height: number }>>({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [localDetail, setLocalDetail] = useState<EntityDetailLevel | undefined>();
  const [localLayoutControls, setLocalLayoutControls] = useState<Preferences["layoutControls"] | undefined>();
  const [localPositions, setLocalPositions] = useState<Record<string, Point>>(
    {},
  );
  const [connectorDrafts, setConnectorDrafts] = useState<
    Record<string, Connector>
  >({});
  const [tool, setTool] = useState<"move" | "select">("move"),
    [menu, setMenu] = useState<ContextMenu | null>(null),
    [preview, setPreview] = useState<Preview | null>(null);
  const [zoom, setZoom] = useState(1),
    [layoutOpen, setLayoutOpen] = useState(false),
    [routeInfo, setRouteInfo] = useState(false);
  const [layoutOptions, setLayoutOptions] = useState<LayoutOptions>({
    mode: "hierarchical",
    direction: "down",
    gap: 90,
  });
  const [savedLayoutsOpen, setSavedLayoutsOpen] = useState(false);
  const [pngScale, setPngScale] = useState<1 | 2 | 4>(2);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportFallback, setExportFallback] = useState<"svg" | "png" | null>(null);
  const canceledDrag = useRef(false);
  const resizeStartBoxes = useRef<CanvasBox[] | null>(null);
  const [scopeSelection, setScopeSelection] = useState(false),
    [status, setStatus] = useState(""),
    [showMinimap, setShowMinimap] = useState(preferences.showMinimap ?? false);
  useEffect(()=>setShowMinimap(preferences.showMinimap??false),[preferences.showMinimap]);
  const fittedDiagram = useRef<string | null>(null);
  const lastCommand = useRef<number | null>(null),
    currentPositions = useRef(localPositions),
    dragging = useRef(false),
    previousDiagram = useRef(diagram?.diagramId);
  const gestureCallback = useRef(onGestureActiveChange); gestureCallback.current=onGestureActiveChange;
  const signalGesture = useCallback((active:boolean)=>{dragging.current=active;gestureCallback.current?.(active);},[]);
  const nodeDrag = useRef<{start:Point;pointer:Point;positions:Record<string,Point>;sizes:Record<string,EntityDimensions>;viewport:Viewport;bounds:DragBounds}|null>(null);
  const edgeDrag = useRef(createEdgeDragController());
  const requestDragFrame = useRef<(() => void) | null>(null);
  const boundedExtent = useRef<{key:string;bounds:DragBounds}|null>(null);
  const pendingGeometryCamera = useRef<{fit:boolean;before:Viewport}|null>(null);
  const previousCameraGeometry = useRef<string>('');
  currentPositions.current = localPositions;
  const objects = useMemo(
    () => new Map(document.objects.map((o) => [o.objectId, o])),
    [document.objects],
  );
  const terms = vocabulary(document, diagram?.representationId, undefined, diagram?.perspective);
  const conceptual = terms.conceptual;
  const notation = diagram?.notation ?? preferences.notation;
  const detailLevel = canvasDetailLevel(preferences, diagram, lockedPreferences, localDetail);
  const entityDisplay = effectiveEntityDisplay(preferences, diagram, lockedPreferences);
  const displayLevel = conceptual && !['name', 'primary', 'business-v2'].includes(detailLevel) ? 'business-v2' : detailLevel;
  const detailChoices = detailLevelOptions(detailLevel, conceptual).map(choice=>({...choice,label:terms.physical?choice.label.replace(/Entity/g,'Table').replace(/entity/g,'table').replace(/attributes/g,'columns'):choice.label}));
  const detailLabel = detailChoices.find(choice=>choice.id===displayLevel)?.label ?? 'Legacy filtered view';
  const layoutControlMode = localLayoutControls ?? preferences.layoutControls ?? "icons";
  hoverBlocked.current = !!(interactionBlocked || toolbarDragging || menu || preview || layoutOpen || savedLayoutsOpen || detailOpen || relationshipPicking || relationshipSource);
  useEffect(() => { if(hoverBlocked.current) closeEntityPreview(); }, [interactionBlocked,toolbarDragging,menu,preview,layoutOpen,savedLayoutsOpen,detailOpen,relationshipPicking,relationshipSource,closeEntityPreview]);
  const dimensionContext=useRef(`${document.model.modelId}:${diagramId}`);
  if(dimensionContext.current!==`${document.model.modelId}:${diagramId}`){ localSizes.current.clear(); dimensionContext.current=`${document.model.modelId}:${diagramId}`; }
  const cameraKey = `${document.model.modelId}:${diagram?.diagramId}`;
  const savedFingerprint = diagram ? geometryFingerprint(diagram) : "";
  // Fingerprint starts with numeric diagramVersion; camera identity excludes
  // that revision without reparsing the complete diagram on every pointer frame.
  const cameraFingerprint = savedFingerprint.slice(savedFingerprint.indexOf(",") + 1);
  useEffect(() => {
    if (previousDiagram.current !== diagram?.diagramId) {
      setLocalPositions({});
      setResizeDrafts({});
      setLocalDetail(undefined);
      setConnectorDrafts({});
      setPreview(null);
      setMenu(null);
      setRouteInfo(false);
      setSavedLayoutsOpen(false);
      setExportFallback(null);
      previousDiagram.current = diagram?.diagramId;
    } else if (!dragging.current) {
      setResizeDrafts({});
      setLocalPositions({});
      setConnectorDrafts({});
    }
  }, [diagram?.diagramId, savedFingerprint]);
  const onMeasure = useCallback(
    (id: string, width: number, height: number) =>
      setSizes((old) =>
        old[id]?.width === width && old[id]?.height === height
          ? old
          : { ...old, [id]: { width, height } },
      ),
    [],
  );
  // Pointer resize drafts never enter committed/export geometry or its routing cache.
  const committedBoxes = useMemo(() => (diagram?.appearances ?? []).filter(a=>objects.has(a.objectId)).map(a=>{
    const object=objects.get(a.objectId)!, children=entityFields(document,object.objectId);
    const stored=JSON.stringify([a.width,a.height,a.sizeLocked]);
    const detail=JSON.stringify([detailLevel,preferences.showEntityDescription,entityDisplay,preferences.attributeDisplayLimit,preferences.showAllAttributes]);
    const text=JSON.stringify([object.name,object.displayName,object.briefDescription,object.description,children.fields,children.keys,preferences.fontFamily,preferences.fontSize,preferences.titleSize,preferences.autoSizeEntities]);
    const previous=localSizes.current.get(a.appearanceId);
    const retained=previous?.stored===stored && (!preferences.resizeOnDetailChange || previous.detail===detail) ? previous.dimensions : undefined;
    const locked=a.sizeLocked ?? (a.width!==undefined || a.height!==undefined);
    const sizingAppearance=preferences.resizeOnDetailChange && previous && previous.detail!==detail && !locked ? {...a,width:undefined,height:undefined} : a;
    let size=entitySize(object,children.fields,children.keys,sizingAppearance,preferences,conceptual,detailLevel,retained,false,terms.physical,entityDisplay);
    // Explicit fit/unlock/layout dimensions establish the new baseline. Opening or unlocking
    // must not immediately fit all hidden rows again; later text changes may grow it.
    if(!preferences.autoSizeEntities && !size.locked && a.width!==undefined && a.height!==undefined && (!previous || previous.stored!==stored)) size={width:a.width,height:a.height,locked:false};
    else if(!preferences.autoSizeEntities && previous?.stored===stored && previous.text===text && (!preferences.resizeOnDetailChange || previous.detail===detail)) size={...previous.dimensions,locked:size.locked};
    localSizes.current.set(a.appearanceId,{dimensions:size,stored,detail,text});
    return {id:a.appearanceId,objectId:a.objectId,x:a.position.x,y:a.position.y,width:size.width,height:size.height,pinned:a.pinned,group:object.tags[0]||object.schema};
  }),[diagram?.appearances,objects,document.objects,preferences,conceptual,detailLevel,terms.physical]);
  const baseBoxes = useMemo(() => committedBoxes.map(box => ({ ...box, ...resizeDrafts[box.id] })), [committedBoxes, resizeDrafts]);
  const boxes = useMemo(
    () =>
      baseBoxes.map((b) => ({
        ...b,
        ...(preview?.candidate.positions[b.id] ?? localPositions[b.id] ?? {}),
      })),
    [baseBoxes, localPositions, preview],
  );
  const layoutFingerprint = `${JSON.stringify([diagram?.layoutCheckpoints, document.objects, document.relationships])}:${savedFingerprint}:${JSON.stringify(baseBoxes.map((b) => [b.id, b.width, b.height, b.group]))}:${JSON.stringify([preferences.fontFamily, preferences.fontSize, preferences.titleSize])}:${JSON.stringify(document.relationships.map((r) => [r.relationshipId, r.sourceObjectId, r.targetObjectId]))}`;
  const layoutSettings = JSON.stringify([
    layoutOptions,
    scopeSelection,
    scopeSelection ? selection : [],
  ]);
  const relationships = useMemo(
    () =>
      document.relationships.filter(
        (r) => r.representationId === diagram?.representationId,
      ),
    [document.relationships, diagram?.representationId],
  );
  const connectors = useMemo(() => {
    const values = new Map(
      (preview?.checkpoint?.connectors ?? (preview ? connectorsForAutoLayout(diagram?.connectors ?? [], relationships,
        preview.options.scopeIds ? baseBoxes.filter(box => preview.options.scopeIds!.includes(box.id) || preview.options.scopeIds!.includes(box.objectId)).map(box => box.objectId) : undefined) : diagram?.connectors ?? [])).map((c) => [c.relationshipId, c]),
    );
    if(!preview && (Object.keys(localPositions).length || Object.keys(resizeDrafts).length)) {
      try { for(const update of connectorUpdatesForMovedBoxes(committedBoxes,boxes,relationships,[...values.values()],(preferences.preserveManualRoutes || preferences.autoAdjustRelationships===false))) {
        values.set(update.relationshipId,{...values.get(update.relationshipId),relationshipId:update.relationshipId,...update.changes});
      } } catch { /* A temporarily overlapping drag is repaired before commit. */ }
    }
    Object.entries(connectorDrafts).forEach(([id, c]) => values.set(id, c));
    return [...values.values()];
  }, [diagram?.connectors, connectorDrafts, preview, relationships, baseBoxes, committedBoxes, boxes, localPositions, resizeDrafts, preferences.autoAdjustRelationships,preferences.preserveManualRoutes]);
  const existingOverlaps = useMemo(()=>findEntityOverlaps(committedBoxes),[committedBoxes]);
  const committedRoutes = useMemo(() => routeRelationships(committedBoxes, relationships, diagram?.connectors ?? [],preferences), [committedBoxes, relationships, diagram?.connectors,preferences.centerRelationshipEndpoints,preferences.avoidParentTopAttachment,preferences.relationshipLaneGap]);
  const routes = useMemo(() => {
    const hasGestureDraft = !!(Object.keys(connectorDrafts).length || Object.keys(localPositions).length || Object.keys(resizeDrafts).length);
    if (!hasGestureDraft && !preview) return committedRoutes;
    return routeRelationships(boxes, relationships, connectors, { ...preferences, optimizeCrossings: !hasGestureDraft });
  }, [boxes, relationships, connectors, connectorDrafts, localPositions, resizeDrafts, preview, committedRoutes,preferences.centerRelationshipEndpoints,preferences.avoidParentTopAttachment,preferences.relationshipLaneGap]);
  const exportState = useMemo(() => {
    if (!diagram) return { render: null, error: "No active diagram" };
    try { return { render: buildDiagramSvg({ document, diagram, preferences, boxes: committedBoxes, routes: committedRoutes, selectedIds: selection, detailLevel, lockedPreferences }), error: "" }; }
    catch (error) { return { render: null, error: error instanceof Error ? error.message : "No diagram content available to export." }; }
  }, [document, diagram, preferences, committedBoxes, committedRoutes, selection, detailLevel]);
  const exportRender = exportState.render;
  const pngDimensions = exportRender ? diagramPngSize(exportRender, pngScale) : null;
  const exportDiagram = async (format: "svg" | "png", copy: boolean) => {
    if (!exportRender || exportBusy || preview) return;
    setExportBusy(true); setMenu(null); setExportFallback(null);
    try {
      if (copy) {
        const result = await copyDiagramExport(exportRender, format, pngScale);
        setStatus(result.message + (exportRender.warnings.length ? ` ${exportRender.warnings.join(" ")}` : ""));
        if (!result.copied) setExportFallback(format);
      } else {
        const filename = `${document.model.name}-${diagram?.name ?? "diagram"}`.replace(/[^a-zA-Z0-9._-]+/g, "-");
        await downloadDiagramExport(exportRender, format, filename, pngScale);
        setStatus(`${format.toUpperCase()} downloaded. ${exportRender.entityCount} ${terms.objects}, ${exportRender.relationshipCount} relationships. ${exportRender.warnings.join(" ")}`);
      }
    } catch (error) { setStatus(error instanceof Error ? error.message : "Export failed. Try SVG or a smaller PNG resolution."); }
    finally { setExportBusy(false); }
  };
  const allDiagramIds = useMemo(() => diagramSelectionIds(diagram, document.objects, relationships), [diagram, document.objects, relationships]);
  const selectedRoute = routes.find((r) =>
    selection.includes(r.relationship.relationshipId),
  );
  const degreeByObject = useMemo(() => {
    const map = new Map<string, number>();
    relationships.forEach((r) =>
      [r.sourceObjectId, r.targetObjectId].forEach((id) =>
        map.set(id, (map.get(id) ?? 0) + 1),
      ),
    );
    return map;
  }, [relationships]);
  const foreignIds = useMemo(
    () => new Set(relationships.flatMap((r) => r.sourceAttributeIds ?? [])),
    [relationships],
  );
  const commit = useCallback(
    (ops: Operation[], summary: string, explicitGeometry = false) => {
      if (!readOnly && ops.length && (!preview || explicitGeometry)) {
        try {
          onOperations(withMovedConnectorUpdates(document, ops, (preferences.preserveManualRoutes || preferences.autoAdjustRelationships === false),
            diagram ? { [diagram.diagramId]: resizeStartBoxes.current ?? committedBoxes } : {}, !explicitGeometry,
            {enabled: !explicitGeometry, pushNeighbors: preferences.autoAdjustNearbyEntities !== false}), summary);
          setStatus(summary);
        } catch(error) { setStatus(error instanceof Error ? error.message : "This placement cannot preserve a valid layout."); setLocalPositions({});setResizeDrafts({});setConnectorDrafts({}); }
      }
    },
    [readOnly, onOperations, document, preferences.preserveManualRoutes, preferences.autoAdjustRelationships, preferences.autoAdjustNearbyEntities, diagram, committedBoxes, preview],
  );
  const onResizeStart = useCallback((_id: string) => {
    if (readOnly || preview) return;
    canceledDrag.current = false;
    resizeStartBoxes.current = committedBoxes.map(box => ({ ...box }));
    signalGesture(true);
    closeEntityPreview();
  }, [readOnly, preview, committedBoxes, closeEntityPreview, signalGesture]);
  const onResize = useCallback((id: string, bounds: Point & { width: number; height: number }, complete: boolean) => {
    if (readOnly || preview || !diagram || canceledDrag.current) return;
    const current = diagram.appearances.find(a=>a.appearanceId===id);
    if (!current || (current.sizeLocked ?? (current.width !== undefined || current.height !== undefined))) return;
    const dimensions = { width: Math.max(240, Math.round(bounds.width)), height: Math.max(96, Math.round(bounds.height)) };
    const appearance = diagram.appearances.find(a => a.appearanceId === id);
    const position = appearance?.pinned ? appearance.position : { x: Math.round(bounds.x), y: Math.round(bounds.y) };
    closeEntityPreview();
    if (!resizeStartBoxes.current) resizeStartBoxes.current = baseBoxes.map(box => ({ ...box }));
    signalGesture(!complete);
    setResizeDrafts((old) => ({ ...old, [id]: dimensions }));
    setSizes((old) => ({ ...old, [id]: dimensions }));
    setLocalPositions((old) => ({ ...old, [id]: position }));
    if (complete) { commit([{ type: "appearance.update", diagramId: diagram.diagramId, appearanceId: id, changes: { ...dimensions, position, sizeLocked: true } }], "Resize entity and lock size"); resizeStartBoxes.current = null; }
  }, [readOnly, preview, diagram, commit, baseBoxes, signalGesture]);
  const changeConnector = useCallback(
    (id: string, connector: Connector, complete: boolean) => {
      if (readOnly || !diagram || preview) return;
      const repaired = repairConnectorRoute(baseBoxes, relationships, connector, connectors);
      if (!repaired) { setStatus("This route cannot avoid the entities. Choose a clear segment or move an entity to make room."); if(complete)setConnectorDrafts({}); return; }
      connector = repaired;
      setConnectorDrafts((old) => ({ ...old, [id]: connector }));
      if (complete)
        commit(
          [{ type: "connector.set", diagramId: diagram.diagramId, connector }],
          "Adjust relationship route",
        );
    },
    [readOnly, diagram, commit, preview, baseBoxes, relationships, connectors],
  );
  const onWaypoint = useCallback(
    (id: string, index: number, point: Point, complete: boolean) => {
      const connector = connectors.find((c) => c.relationshipId === id) ?? {
        relationshipId: id,
      };
      const waypoints = [...(connector.waypoints ?? [])];
      waypoints[index] = { x: Math.round(point.x), y: Math.round(point.y) };
      const route = routes.find(item => item.relationship.relationshipId === id);
      changeConnector(
        id,
        { ...connector, ...(route ? manualConnectorChanges(route, waypoints) : { routing: "manual", waypoints }) },
        complete,
      );
    },
    [connectors, routes, changeConnector],
  );
  const onRouteEdit = useCallback((id: string, route: RoutedRelationship, kind: "segment" | "bend", index: number, delta: Point, complete: boolean) => {
    if (readOnly || preview) return;
    const current = diagram?.connectors.find(connector => connector.relationshipId === id) ?? { relationshipId: id };
    const waypoints = current.routing === "manual" ? routeEditWaypoints(route.points, kind, index, delta)
      : (kind === "bend" ? [route.points[index]!] : [route.points[index]!,route.points[index+1]!])
        .map(point=>({x:Math.round(point.x+delta.x),y:Math.round(point.y+delta.y)}));
    changeConnector(id, { ...current, ...manualConnectorChanges(route, waypoints) }, complete);
    if (!selection.includes(id)) onSelection([id]);
  }, [readOnly, preview, diagram, changeConnector, selection, onSelection]);
  const onEndpoint = useCallback(
    (id: string, end: "source" | "target", point: Point, complete: boolean) => {
      const route = routes.find((r) => r.relationship.relationshipId === id);
      if (!route) return;
      const box = route[end],
        occupied = new Set<string>();
      for (const r of routes)
        for (const which of ["source", "target"] as const)
          if (
            r[which].id === box.id &&
            !(r.relationship.relationshipId === id && which === end)
          ) {
            const a = r[`${which}Attachment`];
            occupied.add(`${a.side}:${a.slot}`);
          }
      const attachment = nearestAttachment(
          box,
          point,
          degreeByObject.get(box.objectId) ?? 1,
          occupied,
        ),
        connector = connectors.find((c) => c.relationshipId === id) ?? {
          relationshipId: id,
        };
      changeConnector(
        id,
        {
          ...connector,
          ...manualConnectorChanges(route, connector.waypoints ?? []),
          [`${end}Side`]: attachment.side,
          [`${end}Slot`]: attachment.slot,
          [`${end}Fraction`]: undefined,
        },
        complete,
      );
    },
    [routes, connectors, degreeByObject, changeConnector],
  );
  const showMenu = useCallback((next: ContextMenu) => {
    closeEntityPreview();
    const bounds = container.current?.getBoundingClientRect();
    if (!bounds) return;
    setMenu({
      ...next,
      x: next.x - bounds.left,
      y: next.y - bounds.top,
    });
  }, [closeEntityPreview]);
  const onRouteMenu = useCallback(
    (id: string, x: number, y: number, point: Point, waypoint?: number) => {
      if (!selection.includes(id)) onSelection([id]);
      showMenu({ kind: "edge", id, x, y, point, waypoint });
    },
    [onSelection, showMenu, selection],
  );
  const nodes = useMemo<EntityNode[]>(
    () =>
      boxes.map((box) => {
        const object = objects.get(box.objectId)!,
          appearance = diagram!.appearances.find(
            (a) => a.appearanceId === box.id,
          )!;
        const children = document.objects.filter(
          (o) => o.ownerObjectId === object.objectId,
        );
        const content = resolveEntityContent(document, object.objectId, detailLevel, preferences.showEntityDescription, diagram?.perspective,entityDisplay,preferences);
        const allFields = content.fields;
        const keys = children.filter((o) => o.kind === "key");
        return {
          id: box.id,
          type: "entity",
          className: `nopan ${relationshipSource === object.objectId ? "is-relationship-source" : ""}`,
          position: { x: box.x, y: box.y },
          width: box.width,
          height: box.height,
          measured: { width: box.width, height: box.height },
          selected:
            selection.includes(object.objectId) ||
            children.some((c) => selection.includes(c.objectId)),
          draggable: !readOnly && !box.pinned && !preview && !relationshipPicking,
          deletable: false,
          connectable: false,
          ariaLabel: `${object.kind} ${object.name}, ${children.filter((c) => c.kind !== "key").length} fields${box.pinned ? ", position pinned" : ""}`,
          data: {
            object,
            content,
            fields: content.visibleFields,
            totalFields: allFields.length,
            keys,
            detailLevel,
            colors: getEntityColors(preferences, document, diagram!, appearance),
            displayWidth: box.width,
            displayHeight: box.height,
            resizable: !readOnly && !preview && !(appearance.sizeLocked ?? (appearance.width !== undefined || appearance.height !== undefined)),
            sizeLocked: appearance.sizeLocked ?? (appearance.width !== undefined || appearance.height !== undefined),
            onPreview: (objectId:string,x:number,y:number) => { closeEntityPreview(); setEntityPreview({objectId,x,y,mode:'click'}); },
            onHover: (objectId:string,x:number,y:number) => { keepEntityPreview(); hovered.current=objectId; clearTimeout(hoverTimer.current); if(!preferences.entityPreviewEnabled || hoverBlocked.current || dragging.current || entityPreview?.mode==='click')return; hoverTimer.current=setTimeout(()=>{if(hovered.current===objectId&&!dragging.current&&!hoverBlocked.current)setEntityPreview({objectId,x,y,mode:'hover'});}, preferences.entityPreviewDelay); },
            onLeave: leaveEntity,
            onFieldMenu: (fieldId:string,x:number,y:number) => { const group = selection.includes(object.objectId) && selection.length > 1; if (!group) onSelection([fieldId]); showMenu({kind:group ? "selection" : "node",id:box.id,fieldId,x,y,point:flow.screenToFlowPosition({x,y})}); },
            onResize,
            onResizeStart,
            preferences,
            conceptual,
            notation,
            degree: degreeByObject.get(box.objectId) ?? 1,
            pinned: !!box.pinned,
            collapsed: false,
            moveTool: tool === "move",
            duplicate:
              diagram!.appearances.filter((a) => a.objectId === box.objectId)
                .length > 1,
            dependent: relationships.some(
              (r) => r.sourceObjectId === box.objectId && r.identifying,
            ),
            selectedFieldIds: selection,
            foreignIds,
            onField: (id: string) => { if (relationshipPicking || relationshipSource) onRelationshipTarget?.(object.objectId); else onSelection([id]); },
            onOpen: onOpenObject,
            onMeasure,
            onMenu: (id: string, x: number, y: number) => {
              if (!selection.includes(object.objectId)) onSelection([object.objectId]);
              showMenu({
                kind: "node",
                id,
                x,
                y,
                point: { x: box.x, y: box.y },
              });
            },
          },
        };
      }),
    [
      boxes,
      objects,
      menu, layoutOpen, relationshipSource, relationshipPicking, interactionBlocked, entityPreview?.mode, closeEntityPreview, keepEntityPreview, leaveEntity,
      document.objects,
      diagram,
      selection,
      readOnly,
      preview,
      preferences,
      conceptual,
      notation,
      degreeByObject,
      foreignIds,
      onSelection,
      onOpenObject,
      onRelationshipTarget,
      onMeasure,
      showMenu,
      relationships,
      detailLevel,
      resizeDrafts,
      tool,
      onResize,
      onResizeStart,
    ],
  );
  const edges = useMemo<RelationEdge[]>(
    () =>
      routes.map((route) => ({
        id: route.relationship.relationshipId,
        source: route.source.id,
        target: route.target.id,
        sourceHandle: `${route.sourceAttachment.side}:${route.sourceAttachment.slot}`,
        targetHandle: `${route.targetAttachment.side}:${route.targetAttachment.slot}`,
        type: "relationship",
        selected: selection.includes(route.relationship.relationshipId),
        deletable: false,
        reconnectable: false,
        ariaLabel: `${route.relationship.name}: ${objects.get(route.source.objectId)?.name} ${route.relationship.sourceOptional ? "zero" : "one"} to ${route.relationship.sourceCardinality}; ${objects.get(route.target.objectId)?.name} ${route.relationship.targetOptional ? "zero" : "one"} to ${route.relationship.targetCardinality}`,
        data: {
          route,
          boxes,
          notation,
          preferences,
          style: resolveRelationshipStyle(preferences, diagram!, connectors.find((c) => c.relationshipId === route.relationship.relationshipId), route.relationship.identifying),
          connector: connectors.find(
            (c) => c.relationshipId === route.relationship.relationshipId,
          ),
          readOnly: readOnly || !!preview,
          editVersion: cameraFingerprint,
          onWaypoint,
          onRouteEdit,
          onEndpoint,
          onRouteMenu,
          onGestureActiveChange: signalGesture,
          onCancel: () => {
            setConnectorDrafts({});
            setStatus("Route adjustment canceled.");
          },
        },
      })),
    [
      routes,
      savedFingerprint,
      notation,
      connectors,
      readOnly,
      preview,
      selection,
      objects,
      onWaypoint,
      onRouteEdit,
      onEndpoint,
      onRouteMenu,
      preferences,
      diagram,
    ],
  );
  // Dock rails resize ReactFlow itself. Its measured viewport is the usable drawing rectangle;
  // personal font changes never invoke these explicit camera operations.
  const fitDiagram = useCallback((duration = 250, maxZoom?: number) => {
    const viewport = container.current?.querySelector(".react-flow")?.getBoundingClientRect();
    let top = 12, bottom = 44;
    if (viewport) {
      // Home-position toolbars overlay the canvas; docked rails already resize it.
      for (const toolbar of container.current?.querySelectorAll<HTMLElement>(".dg-canvas-toolbox:not(.is-relocated), .dg-canvas-layout-tools:not(.is-relocated)") ?? []) {
        const bounds = toolbar.getBoundingClientRect();
        if (bounds.bottom > viewport.top && bounds.top < viewport.bottom) top = Math.max(top, bounds.bottom - viewport.top + 16);
      }
      const exporter = container.current?.querySelector<HTMLElement>(".dg-canvas-export-tools:not(.is-relocated)")?.getBoundingClientRect();
      if (exporter && exporter.top < viewport.bottom && exporter.bottom > viewport.top) bottom = Math.max(bottom, viewport.bottom - exporter.top + 16);
    }
    if (!viewport) return Promise.resolve(false);
    const geometry: CanvasBox[]=[...boxes];
    for(const route of routes) {
      const style=resolveRelationshipStyle(preferences,diagram!,connectors.find(c=>c.relationshipId===route.relationship.relationshipId),route.relationship.identifying);
      if(preferences.showRelationshipNames!==false && style.label.visible!==false) {
        const caption=style.label.source==='description'&&route.relationship.description?.trim()?route.relationship.description:route.relationship.name;
        const label=layoutRelationshipLabel(route.points,style.label,caption,preferences.fontFamily,11,boxes),vertical=Math.abs(label.angle)===90;
        geometry.push({id:`label-${route.relationship.relationshipId}`,objectId:'',x:label.x-(vertical?label.height:label.width)/2,y:label.y-(vertical?label.width:label.height)/2,width:vertical?label.height:label.width,height:vertical?label.width:label.height});
      }
      for(const p of route.points)geometry.push({id:'route',objectId:'',...p,width:1,height:1});
    }
    return flow.setViewport(fittedViewport(geometry,viewport.width,viewport.height,{top,right:14,bottom,left:14},maxZoom??2),{duration});
  }, [flow,boxes,routes,preferences,diagram,connectors]);
  const restoreViewport = useCallback((viewport: Viewport) => flow.setViewport(viewport), [flow]);
  const runLayout = useCallback(
    (options: LayoutOptions = layoutOptions) => {
      if (readOnly || !diagram) {
        setStatus("Layout changes require diagram-edit permission.");
        return;
      }
      const scoped = {
        ...options,
        scopeIds: scopeSelection
          ? selection.filter((id) =>
              baseBoxes.some((b) => b.id === id || b.objectId === id),
            )
          : undefined,
      };
      if (scopeSelection && !scoped.scopeIds?.length) {
        setStatus("Select at least one entity to arrange a selection.");
        return;
      }
      const candidate = createLayout(
        baseBoxes,
        relationships,
        scoped,
        layoutFingerprint,
      );
      setPreview((old) => ({
        candidate,
        options: scoped,
        base: layoutFingerprint,
        settings: JSON.stringify([
          options,
          scopeSelection,
          scopeSelection ? selection : [],
        ]),
        viewport: old?.viewport ?? flow.getViewport(),
      }));
      setSavedLayoutsOpen(false);
      setLayoutOpen(true);
      setStatus("Layout preview only. Apply to accept one undoable change.");
      requestAnimationFrame(() =>
        fitDiagram(),
      );
    },
    [
      readOnly,
      diagram,
      layoutOptions,
      scopeSelection,
      selection,
      baseBoxes,
      relationships,
      layoutFingerprint,
      flow,
      fitDiagram,
    ],
  );
  const executeGeometryCommand = useCallback((type:'compact'|'auto-fit') => {
    if(!diagram||readOnly||preview||dragging.current)return;
    try {
      const after=type==='compact'?compactBoxes(committedBoxes):autoFitDiagram(document,diagram,preferences,committedBoxes);
      const operations=diagramGeometryOperations(document,diagram,committedBoxes,after,preferences);
      if(!operations.length){void fitDiagram();setStatus(type==='compact'?'Diagram spacing is already compact.':'All boxes already fit the selected content.');return;}
      pendingGeometryCamera.current={fit:true,before:flow.getViewport()};
      geometryCameras.set(`${cameraKey}:${cameraFingerprint}`,flow.getViewport());
      localSizes.current.clear();
      commit(operations,type==='compact'?'Compact diagram':'Auto-fit displayed entity content',true);
    } catch(error){setStatus(error instanceof Error?error.message:'Unable to fit this diagram.');}
  },[diagram,readOnly,preview,committedBoxes,document,preferences,fitDiagram,flow,cameraKey,cameraFingerprint,commit]);
  // History is keyed by geometry, so background revisions cannot move the camera.
  // One Undo restores both the transaction's geometry and its useful viewport.
  useEffect(()=>{
    const key=`${cameraKey}:${cameraFingerprint}`;
    if(previousCameraGeometry.current===key)return;
    previousCameraGeometry.current=key;
    const pending=pendingGeometryCamera.current;
    pendingGeometryCamera.current=null;
    if(pending?.fit){requestAnimationFrame(()=>{void fitDiagram(0).then(()=>geometryCameras.set(key,flow.getViewport()));});}
    else if(pending){geometryCameras.set(key,flow.getViewport());}
    else {const camera=geometryCameras.get(key);if(camera)void flow.setViewport(camera);else geometryCameras.set(key,flow.getViewport());}
    if(geometryCameras.size>300)geometryCameras.delete(geometryCameras.keys().next().value!);
  },[cameraKey,cameraFingerprint,fitDiagram,flow]);

  const visiblePlacement=useCallback((size:EntityDimensions,preferred?:Point):Point=>{
    const rect=container.current?.querySelector('.react-flow')?.getBoundingClientRect();
    if(!rect)return preferred??{x:80,y:80};
    let top=24,bottom=52;
    for(const toolbar of container.current?.querySelectorAll<HTMLElement>('.dg-canvas-toolbox:not(.is-relocated), .dg-canvas-layout-tools:not(.is-relocated)')??[]){const r=toolbar.getBoundingClientRect();if(r.bottom>rect.top&&r.top<rect.bottom)top=Math.max(top,r.bottom-rect.top+20);}
    const exporter=container.current?.querySelector<HTMLElement>('.dg-canvas-export-tools:not(.is-relocated)')?.getBoundingClientRect();if(exporter&&exporter.top<rect.bottom)bottom=Math.max(bottom,rect.bottom-exporter.top+20);
    const view=flow.getViewport(),left=24,right=24;
    const visible={x:(left-view.x)/view.zoom,y:(top-view.y)/view.zoom,width:Math.max(1,rect.width-left-right)/view.zoom,height:Math.max(1,rect.height-top-bottom)/view.zoom};
    const found=visibleEntityPosition(committedBoxes,size,visible,preferred);
    if(found)return found;
    // Expand the inspected rectangle until a clear place exists, then reveal just
    // that local context. Fully occupied or very narrow views still create visibly.
    for(let factor=1.5;factor<=8;factor+=.5){
      const expanded={x:visible.x-visible.width*(factor-1)/2,y:visible.y-visible.height*(factor-1)/2,width:visible.width*factor,height:visible.height*factor};
      const position=visibleEntityPosition(committedBoxes,size,expanded,preferred);if(!position)continue;
      const geometry=[{id:'visible',objectId:'',...visible},{id:'new',objectId:'',...position,...size}];
      void flow.setViewport(fittedViewport(geometry,rect.width,rect.height,{left,right,top,bottom},view.zoom),{duration:0});
      return position;
    }
    const position={x:Math.max(visible.x,...committedBoxes.map(b=>b.x+b.width))+40,y:visible.y};
    void flow.setViewport(fittedViewport([{id:'new',objectId:'',...position,...size}],rect.width,rect.height,{left,right,top,bottom},view.zoom),{duration:0});
    return position;
  },[flow,committedBoxes]);
  useEffect(()=>{onViewportPlacement?.(visiblePlacement);return()=>onViewportPlacement?.(null);},[onViewportPlacement,visiblePlacement]);

  useEffect(() => {
    if (!command || lastCommand.current === command.id) return;
    lastCommand.current = command.id;
    if (command.type === "auto-fit" || command.type === "compact") executeGeometryCommand(command.type);
    if (command.type === "fit")
      void fitDiagram();
    if (command.type === "zoom-in") void flow.zoomIn({ duration: 150 });
    if (command.type === "zoom-out") void flow.zoomOut({ duration: 150 });
    if (command.type === "select-all")
      onSelection(allDiagramIds);
    if (
      command.type === "layout-hierarchical" ||
      command.type === "layout-clustered"
    ) {
      const options = {
        ...layoutOptions,
        mode:
          command.type === "layout-hierarchical"
            ? ("hierarchical" as const)
            : ("clustered" as const),
      };
      setLayoutOptions(options);
      runLayout(options);
    }
  }, [command, flow, baseBoxes, allDiagramIds, onSelection, layoutOptions, runLayout, fitDiagram, executeGeometryCommand]);
  useLayoutEffect(() => {
    const element = contextMenuElement.current, canvas = container.current;
    if (!menu || !element || !canvas) return;
    const place = () => {
      const space = diagramContextMenuSpace(canvas.getBoundingClientRect(), {
        width: window.innerWidth, height: window.innerHeight,
      });
      // Apply the available space before measuring: wrapped rows and the scroll
      // bar contribute to the actual dimensions used for edge placement.
      element.style.maxWidth = `${space.width}px`;
      element.style.maxHeight = `${space.height}px`;
      const position = diagramContextMenuPosition(menu, element.getBoundingClientRect(), space);
      element.style.left = `${position.x}px`;
      element.style.top = `${position.y}px`;
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(element);
    observer.observe(canvas);
    window.addEventListener("resize", place);
    return () => { observer.disconnect(); window.removeEventListener("resize", place); };
  }, [menu, contextMenuFontSize]);
  useEffect(() => {
    if (!menu) return;
    const dismiss = (e: MouseEvent) => {
      if (!(e.target as Element).closest(".dg-canvas-context")) setMenu(null);
    };
    const frame = requestAnimationFrame(() =>
      contextMenuElement.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus({ preventScroll: true }),
    );
    window.addEventListener("pointerdown", dismiss);
    return () => { cancelAnimationFrame(frame); window.removeEventListener("pointerdown", dismiss); };
  }, [menu]);
  const onNodesChange = useCallback(
    (changes: NodeChange<EntityNode>[]) => {
      const updates = changes.filter(
        (c): c is Extract<NodeChange<EntityNode>, { type: "position" }> =>
          c.type === "position" && !!c.position,
      );
      if (updates.length && !nodeDrag.current && !readOnly && !preview && !canceledDrag.current)
        setLocalPositions((old) => ({
          ...old,
          ...Object.fromEntries(updates.map((c) => [c.id, c.position!])),
        }));
      const selectedChanges = changes.filter(
        (c): c is Extract<NodeChange<EntityNode>, { type: "select" }> =>
          c.type === "select",
      );
      if (selectedChanges.length && tool === "select") {
        const selected = new Set(selection);
        for (const c of selectedChanges) {
          const objectId = diagram?.appearances.find(
            (a) => a.appearanceId === c.id,
          )?.objectId;
          if (objectId) {
            if (c.selected) selected.add(objectId);
            else selected.delete(objectId);
          }
        }
        onSelection([...selected]);
      }
    },
    [readOnly, preview, tool, selection, diagram, onSelection],
  );
  const cancelPreview = () => {
    const viewport = preview?.viewport;
    setPreview(null);
    setLayoutOpen(false);
    if (viewport) requestAnimationFrame(() => void flow.setViewport(viewport));
  };
  const previewStale =
    !!preview &&
    (preview.base !== layoutFingerprint || (!preview.checkpoint && preview.settings !== layoutSettings));
  const applyLayout = () => {
    if (!diagram || !preview || readOnly) return;
    if (previewStale) {
      setStatus("The diagram changed. Recompute this preview before applying.");
      return;
    }
    if (preview.candidate.overlaps) {
      setStatus(
        "Resolve overlapping pinned/fixed entities before applying this layout.",
      );
      return;
    }
    const operations = preview.checkpoint?.operations ?? layoutOperations(
      diagram,
      preview.candidate,
      baseBoxes,
      preferences,
    );
    if (!preview.checkpoint) {
      const nextConnectors = connectorsForAutoLayout(diagram.connectors, relationships,
        preview.options.scopeIds ? baseBoxes.filter(box => preview.options.scopeIds!.includes(box.id) || preview.options.scopeIds!.includes(box.objectId)).map(box => box.objectId) : undefined);
      for (const connector of nextConnectors) {
        if (JSON.stringify(connector) !== JSON.stringify(diagram.connectors.find(item => item.relationshipId === connector.relationshipId)))
          operations.push({ type: "connector.set", diagramId: diagram.diagramId, connector });
      }
    }
    commit(
      operations,
      preview.checkpoint ? `Restore layout: ${preview.checkpoint.name}` : `Apply ${preview.options.mode} layout (${preview.candidate.moved} entities) and measured typography profile`,
      true, // This exact preview already contains its explicit connector policy.
    );
    setPreview(null);
    setLayoutOpen(false);
    setLocalPositions({});
  };
  const restoreCheckpoint = (checkpoint?: LayoutCheckpoint) => {
    if (!diagram || !checkpoint || readOnly) { setStatus("This saved layout is no longer available. Reopen Saved layouts."); return; }
    try {
      const result = previewLayoutCheckpoint(diagram, checkpoint, baseBoxes, relationships);
      setPreview(old => ({ candidate: result.candidate, options: layoutOptions, base: layoutFingerprint, settings: layoutSettings, viewport: old?.viewport ?? flow.getViewport(), checkpoint: { id: checkpoint.checkpointId, name: checkpoint.name, operations: result.operations, connectors: result.connectors, added: result.added, removed: result.removed, pinned: result.pinned, adjusted: result.adjusted } }));
      setSavedLayoutsOpen(false); setLayoutOpen(true); setMenu(null); setConnectorDrafts({});
      setStatus(`Previewing saved layout: ${checkpoint.name}. Apply restores geometry and keeps current model content.`);
      requestAnimationFrame(() => void fitDiagram());
    } catch (error) { setStatus(error instanceof Error ? error.message : "Unable to preview this saved layout."); }
  };
  const saveCheckpoint = (name: string) => {
    if (!diagram || readOnly || preview || dragging.current) return;
    try {
      const checkpoint = captureLayoutCheckpoint(diagram, name, relationships, baseBoxes);
      commit(checkpointSaveOperations(diagram, checkpoint), `Save layout: ${name}`);
    } catch (error) { setStatus(error instanceof Error ? error.message : "Unable to save the layout."); }
  };
  useEffect(()=>{
    let frame=0;
    const move=(event:PointerEvent)=>{if(nodeDrag.current)nodeDrag.current.pointer={x:event.clientX,y:event.clientY};};
    const cancel=()=>{const active=nodeDrag.current;if(!active)return;cancelAnimationFrame(frame);frame=0;nodeDrag.current=null;canceledDrag.current=true;edgeDrag.current.reset();setLocalPositions({});void flow.setViewport(active.viewport);signalGesture(false);};
    const tick=(now:number)=>{
      frame=0;
      const active=nodeDrag.current;
      if(!active)return;
      const rect=container.current?.querySelector('.react-flow')?.getBoundingClientRect();
      if(rect){
        const pointer={x:active.pointer.x-rect.left,y:active.pointer.y-rect.top},view=flow.getViewport();
        const next=edgeDrag.current.step(now,pointer,rect,view,active.bounds);
        if(next.x!==view.x||next.y!==view.y||next.zoom!==view.zoom)void flow.setViewport(next,{duration:0});
        const point={x:(pointer.x-next.x)/next.zoom,y:(pointer.y-next.y)/next.zoom};
        const raw={x:point.x-active.start.x,y:point.y-active.start.y};
        const delta={x:preferences.snap?Math.round(raw.x/16)*16:raw.x,y:preferences.snap?Math.round(raw.y/16)*16:raw.y};
        // Clamp the selection as a group, retaining every relative offset.
        for(const [id,start] of Object.entries(active.positions)){const clamped=clampDragPosition({x:start.x+delta.x,y:start.y+delta.y},active.sizes[id]!,active.bounds);delta.x=clamped.x-start.x;delta.y=clamped.y-start.y;}
        const positions=Object.fromEntries(Object.entries(active.positions).map(([id,start])=>[id,{x:start.x+delta.x,y:start.y+delta.y}]));
        currentPositions.current={...currentPositions.current,...positions};
        setLocalPositions(old=>Object.entries(positions).some(([id,p])=>old[id]?.x!==p.x||old[id]?.y!==p.y)?{...old,...positions}:old);
      }
      if(nodeDrag.current)frame=requestAnimationFrame(tick);
    };
    requestDragFrame.current=()=>{if(!frame&&nodeDrag.current)frame=requestAnimationFrame(tick);};
    window.addEventListener('pointermove',move);window.addEventListener('pointercancel',cancel);window.addEventListener('blur',cancel);
    return()=>{requestDragFrame.current=null;cancelAnimationFrame(frame);window.removeEventListener('pointermove',move);window.removeEventListener('pointercancel',cancel);window.removeEventListener('blur',cancel);if(nodeDrag.current)cancel();gestureCallback.current?.(false);};
  },[flow,preferences.snap,signalGesture,diagramId]);
  const beginNodeDrag=(event:MouseEvent|TouchEvent|React.MouseEvent, moved:EntityNode[])=>{
    if(readOnly||preview)return;
    const pointer='touches' in event ? {x:event.touches[0]!.clientX,y:event.touches[0]!.clientY}:{x:event.clientX,y:event.clientY};
    const key=`${document.model.modelId}:${diagramId}`;
    if(boundedExtent.current?.key!==key)boundedExtent.current={key,bounds:dragBounds(committedBoxes)};
    const starts=Object.fromEntries(moved.filter(node=>!node.data.pinned).map(node=>[node.id,{...node.position}]));
    nodeDrag.current={start:flow.screenToFlowPosition(pointer),pointer,positions:starts,sizes:Object.fromEntries(committedBoxes.map(box=>[box.id,{width:box.width,height:box.height}])),viewport:flow.getViewport(),bounds:preferences.allowUnlimitedCanvasExpansion?dragBounds([],true):boundedExtent.current.bounds};
    geometryCameras.set(`${cameraKey}:${cameraFingerprint}`,flow.getViewport());
    edgeDrag.current.reset();canceledDrag.current=false;signalGesture(true);closeEntityPreview();setMenu(null);requestDragFrame.current?.();
  };
  const finishNodeDrag = (moved: EntityNode[]) => {
    // React Flow calls both node and selection stop handlers for a group drag.
    // Consume the gesture once so its second callback cannot add a duplicate
    // history entry with the already-moved positions as its inverse.
    const active=nodeDrag.current;
    if(!active)return;
    nodeDrag.current=null;edgeDrag.current.reset();signalGesture(false);
    moved=moved.map(node=>({...node,position:currentPositions.current[node.id]??node.position}));
    if (!diagram || readOnly || preview || canceledDrag.current) { setLocalPositions({}); return; }
    const ops: Operation[] = moved.filter(node => {
      const saved = diagram.appearances.find(item => item.appearanceId === node.id);
      return saved && !saved.pinned && distance(saved.position, node.position) > 0.5;
    }).map(node => ({ type: "appearance.update", diagramId: diagram.diagramId, appearanceId: node.id, changes: { position: { x: Math.round(node.position.x), y: Math.round(node.position.y) } } }));
    if (!ops.length) { setLocalPositions({}); return; }
    pendingGeometryCamera.current={fit:false,before:active?.viewport??flow.getViewport()};
    commit(ops, `Move ${ops.length === 1 ? moved.find(node => node.id === (ops[0] as { appearanceId: string }).appearanceId)?.data.object.name ?? terms.object : `${ops.length} ${terms.objects}`}`);
  };
  const resetRoute = (id: string) => {
    const c = connectors.find((c) => c.relationshipId === id) ?? {
      relationshipId: id,
    };
    const { sourceSide, sourceSlot, sourceFraction, targetSide, targetSlot, targetFraction, ...rest } = c;
    changeConnector(id, { ...rest, waypoints: [], routing: "auto" }, true);
    setMenu(null);
  };
  const editSide = (id: string, end: "source" | "target", value: string) => {
    const c = {
      ...(connectors.find((c) => c.relationshipId === id) ?? {
        relationshipId: id,
      }),
    };
    if (value === "auto") {
      delete c[`${end}Side`];
      delete c[`${end}Slot`];
      delete c[`${end}Fraction`];
    } else { c[`${end}Side`] = value as Side; delete c[`${end}Fraction`]; c.routing = "manual"; }
    changeConnector(id, c, true);
  };
  const crossings = useMemo(() => countRouteCrossings(routes), [routes]);
  const totalWarnings = routes.filter((r) => r.warnings.length).length,
    selectedNode =
      menu?.kind === "node"
        ? diagram?.appearances.find((a) => a.appearanceId === menu.id)
        : undefined;
  const selectedBoxes = baseBoxes.filter((box) => nodes.some((node) => node.id === box.id && node.selected));
  const arrange = (action: Alignment) => {
    if (!diagram || readOnly || preview) return;
    const positions = arrangeBoxes(selectedBoxes, action);
    const ops: Operation[] = Object.entries(positions).filter(([id, position]) => {
      const box = baseBoxes.find((b) => b.id === id)!;
      return distance(box, position) > .1;
    }).map(([appearanceId, position]) => ({ type: "appearance.update", diagramId: diagram.diagramId, appearanceId, changes: { position, width: baseBoxes.find(b=>b.id===appearanceId)!.width, height: baseBoxes.find(b=>b.id===appearanceId)!.height, sizeLocked: diagram.appearances.find(a=>a.appearanceId===appearanceId)?.sizeLocked ?? (diagram.appearances.find(a=>a.appearanceId===appearanceId)?.width !== undefined || diagram.appearances.find(a=>a.appearanceId===appearanceId)?.height !== undefined) } }));
    commit(ops, `${action === "horizontal" || action === "vertical" ? "Distribute" : "Align"} selected entities ${action}`);
  };
  const fitModelToText = () => {
    if (readOnly || preview) return;
    const operations = fitModelEntitiesToText(document, userPreferences);
    if (!operations.length) { setStatus("Unlocked entities already fit their text. Explicit size locks were preserved."); return; }
    localSizes.current.clear();
    commit(operations, "Fit unlocked entities to displayed text across the model");
    setMenu(null);
  };
  const sizeLockCounts = useMemo(() => {
    const eligibleObjects = new Set(document.objects.filter(object => !object.ownerObjectId && ['concept','entity','table','view','collection','semanticEntity','fact','dimension'].includes(object.kind)).map(object=>object.objectId));
    const baselines = new Set(document.representations.filter(rep=>rep.baseline).map(rep=>rep.representationId));
    const eligible = document.diagrams.filter(d=>!baselines.has(d.representationId)).flatMap(d=>d.appearances).filter(a=>eligibleObjects.has(a.objectId));
    return { total:eligible.length, locked:eligible.filter(a=>a.sizeLocked??(a.width!==undefined||a.height!==undefined)).length };
  }, [document.objects,document.representations,document.diagrams]);
  const sizeLockState = sizeLockCounts.locked === 0 ? 'unlocked' : sizeLockCounts.locked === sizeLockCounts.total ? 'locked' : 'mixed';
  const toggleAllEntitySizes = () => {
    if(readOnly || preview || interactionBlocked || dragging.current || !sizeLockCounts.total)return;
    const locked = sizeLockCounts.locked === 0;
    const measured = new Map(committedBoxes.map(box=>[box.id,{width:box.width,height:box.height}]));
    const operations = lockModelEntitySizes(document,userPreferences,locked,measured);
    commit(operations,`${locked?'Lock':'Unlock'} entity sizes across all editable diagrams in this model`);
  };
  const pinEntities = (pinned: boolean, selectedOnly: boolean) => {
    if (!diagram || readOnly || preview) return;
    const ids = new Set((selectedOnly ? selectedBoxes : baseBoxes).map((box) => box.id));
    const ops: Operation[] = diagram.appearances.filter((a) => ids.has(a.appearanceId) && !!a.pinned !== pinned).map((a) => ({ type: "appearance.update", diagramId: diagram.diagramId, appearanceId: a.appearanceId, changes: { pinned } }));
    commit(ops, `${pinned ? "Pin" : "Unpin"} ${selectedOnly ? "selected" : "all"} entities`);
  };
  const chooseDetail = (level: EntityDetailLevel) => {
    if (lockedPreferences.includes("detailLevel") || lockedPreferences.includes("diagramDetailLevels")) return;
    if (!onPreferencesChange) setLocalDetail(level);
    onPreferencesChange?.({ diagramDetailLevels: { ...preferences.diagramDetailLevels, [diagramId]: level } });
    setDetailOpen(false);
    setStatus(`${terms.object} detail: ${detailChoices.find((entry) => entry.id === level)?.label}. Applied to every ${terms.object} in this diagram.`);
  };
  const alignActions: { id: Alignment; label: string; icon: typeof AlignHorizontalJustifyStart }[] = [
    { id: "left", label: "Align left", icon: AlignHorizontalJustifyStart },
    { id: "center", label: "Align horizontal centers", icon: AlignHorizontalJustifyCenter },
    { id: "right", label: "Align right", icon: AlignHorizontalJustifyEnd },
    { id: "top", label: "Align top", icon: AlignVerticalJustifyStart },
    { id: "middle", label: "Align vertical centers", icon: AlignVerticalJustifyCenter },
    { id: "bottom", label: "Align bottom", icon: AlignVerticalJustifyEnd },
    { id: "horizontal", label: "Distribute horizontally", icon: AlignHorizontalSpaceAround },
    { id: "vertical", label: "Distribute vertically", icon: AlignVerticalSpaceAround },
  ];
  const selectedConnector = connectors.find((c) => c.relationshipId === selectedRoute?.relationship.relationshipId);
  const selectedStyle = diagram ? resolveRelationshipStyle(preferences, diagram, selectedConnector) : undefined;
  const changeRelationshipStyle = (style: RelationshipStyle, complete = true) => {
    if (!selectedRoute || !diagram || readOnly || preview) return;
    const connector: Connector = { ...(selectedConnector ?? { relationshipId: selectedRoute.relationship.relationshipId }), style: { ...selectedConnector?.style, ...style, label: style.label ? { ...selectedConnector?.style?.label, ...style.label } : selectedConnector?.style?.label } };
    setConnectorDrafts((old) => ({ ...old, [connector.relationshipId]: connector }));
    if (complete) commit([{ type: "connector.set", diagramId: diagram.diagramId, connector }], "Change relationship appearance");
  };
  useEffect(() => {
    if (
      !diagram ||
      !baseBoxes.length ||
      baseBoxes.some((b) => !sizes[b.id]) ||
      fittedDiagram.current === cameraKey
    )
      return;
    const saved = cameras.get(cameraKey) ?? diagram.viewportDefault;
    const frame = requestAnimationFrame(() => {
      fittedDiagram.current = cameraKey;
      if (saved) void restoreViewport(saved);
      else void fitDiagram(0, 1);
    });
    return () => cancelAnimationFrame(frame);
  }, [cameraKey, diagram, baseBoxes, sizes, flow, fitDiagram, restoreViewport]);
  if (!diagram)
    return (
      <div className="dg-canvas dg-canvas-missing">
        <Layers size={32} />
        <h3>No diagram in this representation</h3>
        <p>Create a diagram or switch to a represented view.</p>
      </div>
    );
  return (
    <div
      ref={container}
      data-theme={preferences.theme}
      tabIndex={0}
      aria-label="Diagram workspace"
      onContextMenu={e => e.preventDefault()}
      onPointerDownCapture={e => { if ((e.target as Element).closest(".react-flow") && !isTextEditingTarget(e.target)) container.current?.focus({ preventScroll: true }); }}
      className={`dg-canvas ${layoutOpen ? "has-layout-preview" : ""} ${relationshipPicking ? "is-relationship-picking" : ""} ${tool === "select" ? "is-select-tool" : ""} ${readOnly ? "is-readonly" : ""}`}
      onKeyDown={(e) => {
        if (isTextEditingTarget(e.target)) return;
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
          e.preventDefault(); e.stopPropagation(); onSelection(allDiagramIds);
          setStatus(`Selected ${baseBoxes.length} ${terms.objects} and ${routes.length} relationships in this diagram.`); return;
        }
        if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) canceledDrag.current = false;
        if (e.key === "Escape") {
          canceledDrag.current = true;
          if (relationshipPicking || relationshipSource) onCancelRelationship?.();
          setSavedLayoutsOpen(false);
          closeEntityPreview();
          setDetailOpen(false);
          setMenu(null);
          cancelPreview();
          setConnectorDrafts({});
          setResizeDrafts({});
          resizeStartBoxes.current = null;
          setLocalPositions({});
          if(nodeDrag.current){void flow.setViewport(nodeDrag.current.viewport);nodeDrag.current=null;edgeDrag.current.reset();}
          signalGesture(false);
          setStatus("Canceled the active preview or gesture.");
        }
        if (e.shiftKey && e.key === "F10") {
          const focused = (e.target as Element).closest<HTMLElement>(
            ".react-flow__node",
          );
          const node = nodes.find((n) => n.id === focused?.dataset.id);
          if (focused && node) {
            e.preventDefault();
            const b = focused.getBoundingClientRect();
            if (!selection.includes(node.data.object.objectId)) onSelection([node.data.object.objectId]);
            showMenu({
              kind: "node",
              id: node.id,
              x: b.left + 20,
              y: b.top + 20,
              point: node.position,
            });
          }
        }
        if (e.key === "F1") {
          e.preventDefault();
          onHelp("diagrams");
        }
      }}
      onKeyUp={(e) => {
        if (
          !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(
            e.key,
          ) ||
          readOnly ||
          preview ||
          dragging.current ||
          !(e.target as Element).closest(".react-flow__node")
        )
          return;
        const ops: Operation[] = Object.entries(currentPositions.current)
          .filter(([id, p]) => {
            const a = diagram.appearances.find((a) => a.appearanceId === id);
            return a && !a.pinned && distance(a.position, p) > 0.1;
          })
          .map(([appearanceId, position]) => ({
            type: "appearance.update",
            diagramId: diagram.diagramId,
            appearanceId,
            changes: { position },
          }));
        commit(ops, "Move selected entities with keyboard");
      }}
    >
      <ReactFlow<EntityNode, RelationEdge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        connectionMode={ConnectionMode.Loose}
        panOnDrag={tool === "move" ? [0, 1] : [1]}
        autoPanOnNodeDrag={false}
        autoPanOnConnect={false}
        selectionOnDrag={tool === "select"}
        selectionMode={SelectionMode.Full}
        selectionKeyCode={null}
        multiSelectionKeyCode={["Shift", "Meta", "Control"]}
        panActivationKeyCode="Space"
        nodesConnectable={false}
        edgesReconnectable={false}
        elevateEdgesOnSelect
        nodesDraggable={!readOnly && !preview && !relationshipPicking}
        deleteKeyCode={null}
        zoomOnDoubleClick={false}
        minZoom={0.02}
        maxZoom={2}
        snapToGrid={preferences.snap}
        snapGrid={[16, 16]}
        preventScrolling
        defaultViewport={cameras.get(cameraKey) ?? diagram.viewportDefault}
        onMoveEnd={(_, viewport) => {
          if(!nodeDrag.current)signalGesture(false);
          setZoom(viewport.zoom);
          cameras.set(cameraKey, viewport);
          if(!nodeDrag.current&&!pendingGeometryCamera.current)geometryCameras.set(`${cameraKey}:${cameraFingerprint}`,viewport);
        }}
        onPaneClick={() => {
          container.current?.focus({ preventScroll: true });
          onSelection([]);
          setDetailOpen(false);
          setMenu(null);
        }}
        onNodeClick={(event, node) => {
          setMenu(null);
          if (relationshipPicking || relationshipSource) { onRelationshipTarget?.(node.data.object.objectId); return; }
          if (event.shiftKey || event.metaKey || event.ctrlKey)
            onSelection(
              selection.includes(node.data.object.objectId)
                ? selection.filter((id) => id !== node.data.object.objectId)
                : [...selection, node.data.object.objectId],
            );
          else onSelection([node.data.object.objectId]);
        }}
        onSelectionStart={() => { signalGesture(true);closeEntityPreview(); }}
        onSelectionEnd={() => {
          signalGesture(false);
          const chosen = flow
            .getNodes()
            .filter((n) => n.selected)
            .map((n) => n.data.object.objectId);
          if (tool === "select") onSelection(chosen);
        }}
        onSelectionContextMenu={(event) => {
          event.preventDefault();
          showMenu({ kind: "selection", x: event.clientX, y: event.clientY, point: flow.screenToFlowPosition({ x: event.clientX, y: event.clientY }) });
        }}
        onNodeDoubleClick={(_, node) => { if (!relationshipPicking && !relationshipSource) onOpenObject(node.data.object.objectId); }}
        onMoveStart={() => { signalGesture(true);closeEntityPreview(); }}
        onNodeContextMenu={(e, node) => {
          e.preventDefault();
          if (!selection.includes(node.data.object.objectId)) onSelection([node.data.object.objectId]);
          showMenu({
            kind: "node",
            id: node.id,
            x: e.clientX,
            y: e.clientY,
            point: node.position,
          });
        }}
        onNodeDragStart={(event,node,movedNodes)=>beginNodeDrag(event,movedNodes.length?movedNodes:[node])}
        onNodeDragStop={(_, node, movedNodes) => finishNodeDrag(movedNodes.length ? movedNodes : [node])}
        onSelectionDragStart={(event,movedNodes)=>beginNodeDrag(event,movedNodes)}
        onSelectionDragStop={(_, movedNodes) => finishNodeDrag(movedNodes)}
        onEdgeClick={(e, edge) => {
          e.stopPropagation();
          onSelection([edge.id]);
          setMenu(null);
        }}
        onEdgeDoubleClick={(e, edge) => {
          e.stopPropagation();
          onOpenRelationship(edge.id);
        }}
        onEdgeContextMenu={(e, edge) => {
          e.preventDefault();
          onRouteMenu(
            edge.id,
            e.clientX,
            e.clientY,
            flow.screenToFlowPosition({ x: e.clientX, y: e.clientY }),
          );
        }}
        onPaneContextMenu={(e) => {
          e.preventDefault();
          showMenu({
            kind: "canvas",
            x: e.clientX,
            y: e.clientY,
            point: flow.screenToFlowPosition({ x: e.clientX, y: e.clientY }),
          });
        }}
        proOptions={{ hideAttribution: true }}
        aria-label="Data model diagram. Drag empty space to pan; select a relationship once to highlight, twice to edit."
      >
        {preferences.grid && (
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color="var(--border, #c9d4d4)"
          />
        )}
        {showMinimap && (
          <MiniMap
            pannable
            zoomable
            nodeColor="var(--header, #e2eeea)"
            maskColor="color-mix(in srgb, var(--canvas, #edf3f2) 75%, transparent)"
            position="bottom-right"
          />
        )}
      </ReactFlow>
      {relationshipPicking && <div className="dg-canvas-pick-guidance" role="status"><GitBranch size={15}/><span>{relationshipSource ? `Choose target entity. Source: ${objects.get(relationshipSource)?.name ?? "selected entity"}.` : "Choose source entity, then choose target entity."}</span><button onClick={onCancelRelationship}>Cancel</button></div>}
      <div className="dg-canvas-dock-frame"><DockSlot zone="top"/><div className="dg-canvas-side-docks"><DockSlot zone="left"/><DockSlot zone="right"/></div><DockSlot zone="bottom"/></div>
      <DockableToolbar id="navigation" className="dg-canvas-toolbox" label="Canvas interaction">
        <WorkspaceCommand id="move"><button
          className={tool === "move" ? "is-active" : ""}
          aria-pressed={tool === "move"}
          onClick={() => setTool("move")}
          title="Move: drag empty canvas to pan"
        >
          <Hand size={15} />
          <span>Move</span>
        </button></WorkspaceCommand>
        <WorkspaceCommand id="select"><button
          className={tool === "select" ? "is-active" : ""}
          aria-pressed={tool === "select"}
          onClick={() => setTool("select")}
          title={`Select area: drag a rectangle around ${terms.objects}`}
        >
          <MousePointer2 size={15} />
          <span>Select area</span>
        </button></WorkspaceCommand>
        {!hideLayoutControl && (
          <>
            <i />
            <WorkspaceCommand id="layout-preview"><button
              onClick={() => {
                setSavedLayoutsOpen(false);
                if (layoutOpen) cancelPreview(); else runLayout();
                setMenu(null);
              }}
              disabled={readOnly}
              title={
                readOnly
                  ? "Diagram-edit permission required"
                  : "Preview an automatic layout"
              }
            >
              <Network size={15} />
              <span>Layout</span>
              <ChevronDown size={12} />
            </button></WorkspaceCommand>
          </>
        )}
        <i />
        <WorkspaceCommand id="relationship-names"><button aria-label="Hide relationship names" title={preferences.showRelationshipNames === false ? "Show relationship names" : "Hide relationship names"} aria-pressed={preferences.showRelationshipNames === false} onClick={() => onPreferencesChange?.({ showRelationshipNames: preferences.showRelationshipNames === false })}><Eye size={15}/></button></WorkspaceCommand>
        <WorkspaceCommand id="entity-detail"><button aria-label={terms.physical ? "Table detail" : "Entity detail"} aria-expanded={detailOpen} aria-haspopup="menu" title={`${terms.object} detail: ${detailLabel}`} onClick={() => { setDetailOpen(!detailOpen); setMenu(null); }}>
          <Layers size={15} /><span>{terms.object === "table" ? "Table detail" : "Entity detail"}</span><ChevronDown size={12} />
        </button></WorkspaceCommand>
        {!hideLayoutControl && <WorkspaceCommand id="layout-mode"><button aria-label={layoutControlMode === "icons" ? "Use compact layout controls" : "Use icon layout controls"} title={layoutControlMode === "icons" ? "Use compact layout controls" : "Use icon layout controls"} onClick={() => {
          const mode = layoutControlMode === "icons" ? "compact" : "icons";
          setLocalLayoutControls(mode); onPreferencesChange?.({ layoutControls: mode });
        }}><SlidersHorizontal size={15} /></button></WorkspaceCommand>}
      </DockableToolbar>
      {!hideLayoutControl && nodes.length > 0 && (
        <DockableToolbar id="layout" className={`dg-canvas-layout-tools ${layoutControlMode === "compact" ? "is-compact" : ""}`} label="Layout tools">
          {layoutControlMode === "icons" ? <>
            <div className="dg-canvas-control-group">
              {layoutModes.map(mode => <WorkspaceCommand id={`layout-${mode.id}`} key={mode.id}><button key={mode.id} aria-label={`${mode.label} layout`} title={`${mode.label}: ${mode.description}. Preview before applying.`} aria-pressed={!!preview && !preview.checkpoint && preview.options.mode === mode.id} disabled={readOnly} onClick={() => { const options = { ...layoutOptions, mode: mode.id }; setLayoutOptions(options); runLayout(options); }}>
                {mode.id === "hierarchical" ? <ListTree size={16}/> : mode.id === "clustered" ? <CircleDot size={16}/> : mode.id === "tree" ? <TreePine size={16}/> : mode.id === "circular" ? <CircleDot size={16}/> : mode.id === "orthogonal" ? <Grid3X3 size={16}/> : <Network size={16}/>}<small>{mode.label}</small>
              </button></WorkspaceCommand>)}
            </div>
            <div className="dg-canvas-control-group">{alignActions.map(({ id, label, icon: Icon }) => <WorkspaceCommand id={`align-${id}`} key={id}><button key={id} aria-label={label} title={`${label}; pinned ${terms.objects} stay fixed`} disabled={readOnly || !!preview || selectedBoxes.length < (id === "horizontal" || id === "vertical" ? 3 : 2)} onClick={() => arrange(id)}><Icon size={16} /></button></WorkspaceCommand>)}</div>
            <div className="dg-canvas-control-group">
              <WorkspaceCommand id="pin-selected"><button aria-label="Pin selected" title={`Pin selected ${terms.objects}`} disabled={readOnly || !!preview || !selectedBoxes.length} onClick={() => pinEntities(true, true)}><Pin size={15} /><small>Selected</small></button></WorkspaceCommand>
              <WorkspaceCommand id="unpin-selected"><button aria-label="Unpin selected" title={`Unpin selected ${terms.objects}`} disabled={readOnly || !!preview || !selectedBoxes.length} onClick={() => pinEntities(false, true)}><PinOff size={15} /></button></WorkspaceCommand>
              <WorkspaceCommand id="pin-all"><button aria-label="Pin all" title={`Pin all ${terms.objects}`} disabled={readOnly || !!preview} onClick={() => pinEntities(true, false)}><Pin size={15} /><small>All</small></button></WorkspaceCommand>
              <WorkspaceCommand id="unpin-all"><button aria-label="Unpin all" title={`Unpin all ${terms.objects}`} disabled={readOnly || !!preview} onClick={() => pinEntities(false, false)}><PinOff size={15} /><small>All</small></button></WorkspaceCommand>
            </div>
          </> : <>
            <WorkspaceCommand id="arrange-menu"><label>Arrange <select aria-label="Layout action" value="" disabled={readOnly} onChange={(event) => {
              const action = event.target.value;
              if (layoutModes.some(mode => mode.id === action)) { const options: LayoutOptions = { ...layoutOptions, mode: action as LayoutOptions["mode"] }; setLayoutOptions(options); runLayout(options); }
              else if (action === "pin-all" || action === "unpin-all") pinEntities(action === "pin-all", false);
              else if (action === "pin-selected" || action === "unpin-selected") pinEntities(action === "pin-selected", true);
              else arrange(action as Alignment);
            }}>
              <option value="" disabled>Choose action…</option><optgroup label="Automatic layout">{layoutModes.map(mode => <option key={mode.id} value={mode.id}>{mode.label}</option>)}</optgroup>
              <optgroup label="Align selection">{alignActions.map(({id,label}) => <option key={id} value={id} disabled={selectedBoxes.length < (id === "horizontal" || id === "vertical" ? 3 : 2)}>{label}</option>)}</optgroup>
              <optgroup label="Pin positions"><option value="pin-selected" disabled={!selectedBoxes.length}>Pin selected</option><option value="unpin-selected" disabled={!selectedBoxes.length}>Unpin selected</option><option value="pin-all">Pin all</option><option value="unpin-all">Unpin all</option></optgroup>
            </select></label></WorkspaceCommand>
          </>}
          <WorkspaceCommand id="size-lock"><button aria-label={sizeLockCounts.locked>0 ? "Unlock all entity sizes" : "Lock all entity sizes"} aria-pressed={sizeLockState==='mixed'?'mixed':sizeLockState==='locked'} data-size-lock-state={sizeLockState} title={`${sizeLockCounts.locked} of ${sizeLockCounts.total} entity appearances size-locked. ${sizeLockCounts.locked>0?'Unlock':'Lock'} sizes across all editable diagrams in this model. Position pins are unchanged.`} disabled={readOnly||!!preview||interactionBlocked||!sizeLockCounts.total} onClick={toggleAllEntitySizes}>{sizeLockCounts.locked>0?<Unlock size={15}/>:<LockKeyhole size={15}/>}<small>Sizes: {sizeLockState}</small></button></WorkspaceCommand>
          <WorkspaceCommand id="fit-text"><button aria-label="Fit all entities to text" title="Fit unlocked entity sizes across this model; position pins stay in place" disabled={readOnly || !!preview} onClick={fitModelToText}><Maximize2 size={15}/><small>Fit text</small></button></WorkspaceCommand>
          <div className="dg-canvas-control-group"><WorkspaceCommand id="saved-layouts"><button aria-label="Saved layouts" title="Save or restore a named arrangement" onClick={() => { cancelPreview(); setSavedLayoutsOpen(!savedLayoutsOpen); }}><Bookmark size={15}/><small>Saved layouts</small></button></WorkspaceCommand><WorkspaceCommand id="snap"><button aria-label="Snap to grid" title="Snap positions to grid" aria-pressed={preferences.snap} onClick={() => onPreferencesChange?.({ snap: !preferences.snap })}><Grid3X3 size={15} /></button></WorkspaceCommand><WorkspaceCommand id="layout-help"><button aria-label="Layout help" title="Layout help" onClick={() => onHelp("diagrams")}><HelpCircle size={15} /></button></WorkspaceCommand></div>
          {layoutControlMode === "icons" && <WorkspaceCommand id="arrange"><button aria-label="Arrange" title="Combined layout settings" disabled={readOnly} onClick={()=>{onPreferencesChange?.({layoutPreviewMode:"all"});runLayout();}}><SlidersHorizontal size={15}/><small>Arrange</small></button></WorkspaceCommand>}
          <WorkspaceCommand id="all-layouts"><button aria-label="All layout options" title={preferences.layoutPreviewMode === "all" ? "Switch to focused single-layout settings" : "Show all layout choices together"} aria-pressed={preferences.layoutPreviewMode === "all"} onClick={() => { onPreferencesChange?.({ layoutPreviewMode: preferences.layoutPreviewMode === "all" ? "single" : "all" }); if (!layoutOpen) runLayout(); }}><SlidersHorizontal size={15}/><small>{preferences.layoutPreviewMode === "all" ? "Single layout" : "All layouts"}</small></button></WorkspaceCommand>
        </DockableToolbar>
      )}
      <DockableToolbar id="export" className="dg-canvas-export-tools" label="Diagram export">
        <WorkspaceCommand id="export-scope"><span className="dg-export-scope" title={exportState.error || "Selected content, or the whole active diagram including offscreen objects"}>{exportState.error ? "No exportable content" : `${exportRender?.scope === "selection" ? "Selection" : "Whole diagram"}: ${exportRender?.entityCount ?? 0} ${terms.objects}, ${exportRender?.relationshipCount ?? 0} relationships`}</span></WorkspaceCommand>
        <WorkspaceCommand id="copy-svg"><button aria-label="Copy SVG" title="Copy scalable vector artwork" disabled={exportBusy || !!preview || !exportRender || !nodes.length} onClick={() => void exportDiagram("svg", true)}><Copy size={13}/>SVG</button></WorkspaceCommand>
        <WorkspaceCommand id="download-svg"><button aria-label="Download SVG" title="Download SVG for insertion into documents" disabled={exportBusy || !!preview || !exportRender || !nodes.length} onClick={() => void exportDiagram("svg", false)}><Download size={13}/>SVG</button></WorkspaceCommand>
        <WorkspaceCommand id="png-resolution"><label>PNG<select aria-label="PNG resolution" value={pngScale} onChange={event => setPngScale(Number(event.target.value) as 1 | 2 | 4)}>{[1,2,4].map(scale => <option key={scale} value={scale}>{scale}x</option>)}</select></label></WorkspaceCommand>
        <WorkspaceCommand id="copy-png"><button aria-label="Copy PNG" title={pngDimensions?.supported ? `Copy ${pngDimensions.width} × ${pngDimensions.height} pixels` : pngDimensions?.reason} disabled={exportBusy || !!preview || !nodes.length || !pngDimensions?.supported} onClick={() => void exportDiagram("png", true)}><Copy size={13}/>PNG</button></WorkspaceCommand>
        <WorkspaceCommand id="download-png"><button aria-label="Download PNG" title={pngDimensions?.supported ? `Download ${pngDimensions.width} × ${pngDimensions.height} pixels` : pngDimensions?.reason} disabled={exportBusy || !!preview || !nodes.length || !pngDimensions?.supported} onClick={() => void exportDiagram("png", false)}><Download size={13}/>PNG</button></WorkspaceCommand>
      </DockableToolbar>
      {existingOverlaps.length>0 && !preview && <div className="dg-canvas-overlap-notice" role="status">{existingOverlaps.length} overlapping entity pair{existingOverlaps.length===1?'':'s'} in this diagram. <button disabled={readOnly} onClick={()=>runLayout()}>Preview layout repair</button></div>}
      {savedLayoutsOpen && <SavedLayoutsPanel checkpoints={diagram.layoutCheckpoints ?? []} disabled={readOnly || !!preview} onSave={saveCheckpoint} onRestore={restoreCheckpoint} onDelete={checkpoint => commit(checkpointDeleteOperations(diagram, checkpoint.checkpointId), `Delete saved layout: ${checkpoint.name}`)} onClose={() => setSavedLayoutsOpen(false)}/>}
      {detailOpen && <div className="dg-canvas-detail-menu" role="menu" aria-label={`${terms.object} detail level`}>
        <div className="dg-canvas-context-title">Every {terms.object} in this diagram</div>
        {(lockedPreferences.includes("detailLevel") || lockedPreferences.includes("diagramDetailLevels")) && <p className="dg-canvas-small-note">Detail display is controlled by your administrator. Saved personal choices are retained for when this policy is unlocked.</p>}
        <EntityDisplayControls value={entityDisplay} level={detailLevel} showDescription={preferences.showEntityDescription} conceptual={conceptual} physical={terms.physical} lockedKeys={lockedPreferences} onChange={display=>onPreferencesChange?.({diagramEntityDisplay:{...userPreferences.diagramEntityDisplay,[diagramId]:display}})} />
        <div className="dg-canvas-menu-divider" /><button onClick={() => onHelp("entity-detail")}><HelpCircle size={14} />{terms.physical ? "Table" : "Entity"} detail help</button>
      </div>}
      {!hideNotationControl && (
        <div className="dg-canvas-notation">
          <select
            aria-label="Relationship notation"
            value={notation}
            disabled={readOnly}
            onChange={(e) =>
              commit(
                [
                  {
                    type: "diagram.update",
                    diagramId: diagram.diagramId,
                    changes: { notation: e.target.value as Notation },
                  },
                ],
                `Use ${notationNames[e.target.value as Notation]} notation`,
              )
            }
          >
            {Object.entries(notationNames).map(([value, title]) => (
              <option key={value} value={value}>
                {title}
              </option>
            ))}
          </select>
          <button
            onClick={() => onHelp("notation")}
            aria-label="Explain relationship notation"
          >
            <HelpCircle size={15} />
          </button>
        </div>
      )}
      {!nodes.length && (
        <div className="dg-canvas-empty">
          <div className="dg-canvas-empty-icon">
            <Layers size={32} strokeWidth={1.25} />
          </div>
          <span className="dg-canvas-eyebrow">A CLEAR START</span>
          <h2>Your model starts here</h2>
          <p>
            Add your first{" "}
            {terms.object}
            , import a definition, or choose a building block from the toolbar.
          </p>
          <div className="dg-canvas-empty-actions">
            {onAddEntity && <button disabled={!canEditModel} onClick={() => onAddEntity?.()}><Plus size={14} />Add {terms.object}</button>}
            {onImport && <button disabled={!canEditModel} onClick={onImport}>Import</button>}
            {onBuildingBlock && <button disabled={!canEditModel} onClick={onBuildingBlock}><Layers size={14} />Building blocks</button>}
          </div>
          <div className="dg-canvas-empty-hint">
            <Hand size={14} />
            Drag empty space to pan<span>·</span>
            <MousePointer2 size={14} />
            Select to inspect
          </div>
          <button onClick={() => onHelp("getting-started")}>
            A quick guide <CornerDownRight size={14} />
          </button>
        </div>
      )}
      <div className="dg-canvas-bottom-left">
        <div className="dg-canvas-zoom" role="toolbar" aria-label="Canvas zoom">
          <button
            onClick={() => void flow.zoomOut({ duration: 120 })}
            aria-label="Zoom out"
          >
            <Minus size={16} />
          </button>
          <span>{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => void flow.zoomIn({ duration: 120 })}
            aria-label="Zoom in"
          >
            <Plus size={16} />
          </button>
          <i />
          <button
            onClick={() => void fitDiagram()}
            aria-label="Fit model"
            title="Fit visible model; does not maximize workspace"
          >
            <Maximize2 size={16} />
          </button>
        </div>
        <button
          className={`dg-canvas-map-toggle ${showMinimap ? "is-active" : ""}`}
          aria-label={showMinimap ? "Hide map" : "Show map"}
          aria-pressed={showMinimap}
          onClick={() => {setShowMinimap(!showMinimap);onPreferencesChange?.({showMinimap:!showMinimap});}}
          title={showMinimap ? "Hide map" : "Show map"}
        >
          {showMinimap ? <MapIcon size={15}/> : <MapPinOff size={15}/>}
        </button>
      </div>
      {selectedRoute && !routeInfo && !layoutOpen && (
        <button
          className="dg-canvas-selected-route"
          onClick={() => setRouteInfo(true)}
        >
          <GitBranch size={13} />
          {selectedRoute.relationship.name}
          <span>Route details</span>
          <ChevronDown size={12} />
        </button>
      )}
      <div className="dg-canvas-status">
        <span>
          {nodes.length} {terms.objects}
        </span>
        <span>{edges.length} relationships</span>
        {readOnly && (
          <span>
            <LockKeyhole size={11} /> Read only
          </span>
        )}
        {totalWarnings > 0 && (
          <button
            onClick={() => {
              const route = routes.find((r) => r.warnings.length);
              if (route) {
                onSelection([route.relationship.relationshipId]);
                setRouteInfo(true);
              }
            }}
          >
            <AlertTriangle size={12} /> {totalWarnings} route{" "}
            {totalWarnings === 1 ? "warning" : "warnings"}
          </button>
        )}
      </div>
      <div className={status ? "dg-canvas-notice" : "dg-canvas-sr"} role="status" aria-live="polite">
        {status}
        {exportFallback && <button onClick={() => void exportDiagram(exportFallback, false)} disabled={exportBusy}>Download {exportFallback.toUpperCase()} instead</button>}
        {status && <button aria-label="Dismiss canvas message" onClick={() => { setStatus(""); setExportFallback(null); }}><X size={12}/></button>}
      </div>
      {layoutOpen && (
        <div
          className="dg-canvas-layout-panel"
          role="dialog"
          aria-label={preview?.checkpoint ? "Restore saved layout" : "Automatic layout preview"}
        >
          <div className="dg-canvas-panel-heading">
            <div>
              <span className="dg-canvas-eyebrow">{preview?.checkpoint ? "RESTORE ARRANGEMENT" : "AUTOMATIC LAYOUT"}</span>
              <h3>{preview?.checkpoint?.name ?? `${layoutModes.find(mode => mode.id === layoutOptions.mode)?.label ?? "Automatic"} layout`}</h3>
            </div>
            <button aria-label="Close layout" onClick={cancelPreview}>
              <X size={17} />
            </button>
          </div>
          {preview?.checkpoint ? <p className="dg-canvas-small-note">{preview.checkpoint.added} new objects retained; {preview.checkpoint.removed} removed objects skipped; {preview.checkpoint.pinned} pinned objects respected; {preview.checkpoint.adjusted} positions adjusted for current content. Model definitions and relationships are preserved.</p> : preferences.layoutPreviewMode === "all" ? <div className="dg-canvas-layout-modes">
            {layoutModes.map(mode => <button key={mode.id} className={layoutOptions.mode === mode.id ? "is-active" : ""} onClick={() => { const options = {...layoutOptions, mode: mode.id}; setLayoutOptions(options); runLayout(options); }}><Network size={18}/><strong>{mode.label}</strong><small>{mode.description}</small></button>)}
          </div> : <p className="dg-canvas-small-note">{layoutModes.find(mode => mode.id === layoutOptions.mode)?.description}. Choose another method from the layout palette.</p>}
          {!preview?.checkpoint && <>
          {["hierarchical", "tree", "orthogonal"].includes(layoutOptions.mode) && (
            <>
              <label>
                Direction
                <select
                  value={layoutOptions.direction}
                  onChange={(e) =>
                    setLayoutOptions((o) => ({
                      ...o,
                      direction: e.target.value as LayoutOptions["direction"],
                    }))
                  }
                >
                  <option value="down">Top to bottom</option>
                  <option value="right">Left to right</option>
                  <option value="up">Bottom to top</option>
                  <option value="left">Right to left</option>
                </select>
              </label>
              <label>
                Root {terms.object}
                <select
                  value={layoutOptions.rootId ?? ""}
                  onChange={(e) =>
                    setLayoutOptions((o) => ({
                      ...o,
                      rootId: e.target.value || undefined,
                    }))
                  }
                >
                  <option value="">Automatic roots</option>
                  {baseBoxes.map((b) => (
                    <option key={b.id} value={b.id}>
                      {objects.get(b.objectId)?.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          {layoutOptions.mode === "clustered" && (
            <p className="dg-canvas-small-note">
              Groups use {terms.object} tags or schemas, then related neighborhoods.
              Shared {terms.objects} appear once.
            </p>
          )}
          <label>
            Spacing <span>{layoutOptions.gap} px</span>
            <input
              aria-label="Layout spacing"
              type="range"
              min={40}
              max={200}
              step={10}
              value={layoutOptions.gap}
              onChange={(e) =>
                setLayoutOptions((o) => ({ ...o, gap: Number(e.target.value) }))
              }
            />
          </label>
          <label className="dg-canvas-checkbox">
            <input
              type="checkbox"
              checked={scopeSelection}
              onChange={(e) => setScopeSelection(e.target.checked)}
            />{" "}
            Arrange selected {terms.objects} only
          </label>
          <p className="dg-canvas-small-note">
            <Pin size={12} />
            Pinned {terms.objects} stay in place. Preview changes no model content.
            Apply publishes the current uniform typography measurements with
            shared positions.
          </p>
          </>}
          {preview && (
            <>
              <div className="dg-canvas-preview-metrics">
                <div>
                  <strong>{preview.candidate.moved}</strong>
                  <span>{terms.objects} moved</span>
                </div>
                <div>
                  <strong>{crossings}</strong>
                  <span>edge crossings</span>
                </div>
                <div
                  className={preview.candidate.overlaps ? "has-warning" : ""}
                >
                  <strong>{preview.candidate.overlaps}</strong>
                  <span>overlaps</span>
                </div>
              </div>
              {preview.candidate.warnings.map((w) => (
                <p key={w} className="dg-canvas-layout-warning">
                  <AlertTriangle size={13} />
                  {w}
                </p>
              ))}
              {previewStale && (
                <p className="dg-canvas-layout-warning">
                  The diagram, font measurements, or settings changed. Recompute
                  before applying.
                </p>
              )}
              <p className="dg-canvas-small-note">
                Crossings can remain in dense or cyclic graphs. Review the
                visible routes before applying.
              </p>
            </>
          )}
          <div className="dg-canvas-panel-actions">
            <button onClick={cancelPreview}>Cancel</button>
            <button onClick={() => preview?.checkpoint ? restoreCheckpoint(diagram.layoutCheckpoints?.find(item => item.checkpointId === preview.checkpoint!.id)) : runLayout()}>
              {preview ? "Recompute" : "Preview layout"}
            </button>
            {preview && (
              <button
                className="dg-canvas-primary"
                disabled={
                  previewStale || preview.candidate.overlaps > 0 || readOnly
                }
                onClick={applyLayout}
              >
                <Check size={14} />
                Apply
              </button>
            )}
          </div>
        </div>
      )}
      {selectedRoute && routeInfo && !layoutOpen && (
        <div className="dg-canvas-route-panel">
          <div className="dg-canvas-panel-heading">
            <div>
              <span className="dg-canvas-eyebrow">RELATIONSHIP</span>
              <h3>{selectedRoute.relationship.name}</h3>
            </div>
            <button
              aria-label="Close route details"
              onClick={() => setRouteInfo(false)}
            >
              <X size={16} />
            </button>
          </div>
          <p>
            {objects.get(selectedRoute.source.objectId)?.name}
            <span className="dg-canvas-route-arrow"> → </span>
            {objects.get(selectedRoute.target.objectId)?.name}
          </p>
          <div className="dg-canvas-route-sides">
            {(["source", "target"] as const).map((end) => (
              <label key={end}>
                {end === "source" ? "Source" : "Target"} side
                <select
                  disabled={readOnly || !!preview}
                  value={
                    connectors.find(
                      (c) =>
                        c.relationshipId ===
                        selectedRoute.relationship.relationshipId,
                    )?.[`${end}Side`] ?? "auto"
                  }
                  onChange={(e) =>
                    editSide(
                      selectedRoute.relationship.relationshipId,
                      end,
                      e.target.value,
                    )
                  }
                >
                  <option value="auto">
                    Automatic ({selectedRoute[`${end}Attachment`].side})
                  </option>
                  {SIDES.map((s) => (
                    <option key={s} value={s}>
                      {s[0]!.toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          {selectedStyle && <fieldset className="dg-canvas-relationship-style" disabled={readOnly || !!preview}>
            <legend>Line and label appearance</legend>
            <label>Label source<select aria-label="Relationship label source" value={selectedStyle.label.source} onChange={e=>changeRelationshipStyle({label:{source:e.target.value as "name"|"description"}})}><option value="name">Name</option><option value="description">Description</option></select></label>
            <div className="dg-canvas-style-grid">
              <label>Line color<input aria-label="Relationship line color" type="color" value={selectedStyle.color} onChange={(e) => changeRelationshipStyle({ color: e.target.value }, false)} onBlur={(e) => changeRelationshipStyle({ color: e.target.value })} /></label>
              <label>Line thickness<input aria-label="Relationship line thickness" type="number" min={.5} max={12} step={.5} value={selectedStyle.width} onChange={(e) => changeRelationshipStyle({ width: Math.max(.5, Math.min(12, Number(e.target.value))) }, false)} onBlur={(e) => changeRelationshipStyle({ width: Math.max(.5, Math.min(12, Number(e.target.value))) })} /></label>
              <label>Orientation<select aria-label="Relationship label orientation" value={selectedStyle.label.orientation} onChange={(e) => changeRelationshipStyle({ label: { orientation: e.target.value as "horizontal" | "vertical" | "aligned" } })}><option value="aligned">Along line</option><option value="horizontal">Horizontal</option><option value="vertical">Vertical</option></select></label>
              <label>Placement<select aria-label="Relationship label placement" value={selectedStyle.label.placement} onChange={(e) => changeRelationshipStyle({ label: { placement: e.target.value as "above" | "below" | "on-line" } })}><option value="above">Above / left</option><option value="below">Below / right</option><option value="on-line">On line</option></select></label>
              <label>Distance from line<input aria-label="Relationship label offset" type="number" min={0} max={200} disabled={selectedStyle.label.placement === "on-line"} value={selectedStyle.label.offset} onChange={(e) => changeRelationshipStyle({ label: { offset: Math.max(0, Math.min(200, Number(e.target.value))) } }, false)} onBlur={(e) => changeRelationshipStyle({ label: { offset: Math.max(0, Math.min(200, Number(e.target.value))) } })} /></label>
              <label>Along route (%)<input aria-label="Relationship label position" type="number" min={0} max={100} value={Math.round(selectedStyle.label.position * 100)} onChange={(e) => changeRelationshipStyle({ label: { position: Math.max(0, Math.min(100, Number(e.target.value))) / 100 } }, false)} onBlur={(e) => changeRelationshipStyle({ label: { position: Math.max(0, Math.min(100, Number(e.target.value))) / 100 } })} /></label>
            </div>
            <label className="dg-canvas-checkbox"><input type="checkbox" checked={selectedStyle.label.visible} onChange={(e) => changeRelationshipStyle({ label: { visible: e.target.checked } })} />Show relationship label</label>
            <button className="dg-canvas-reset-style" onClick={() => {
              const connector = { ...(selectedConnector ?? { relationshipId: selectedRoute.relationship.relationshipId }) };
              delete connector.style;
              setConnectorDrafts((old) => ({ ...old, [connector.relationshipId]: connector }));
              commit([{ type: "connector.set", diagramId: diagram.diagramId, connector }], "Reset relationship appearance");
            }}><RotateCcw size={13} />Use inherited appearance</button>
          </fieldset>}
          <p className="dg-canvas-small-note">
            <Info size={12} />
            {Math.round(selectedRoute.length)} px ·{" "}
            {Math.max(0, selectedRoute.points.length - 2)} bends. Endpoints keep
            their own cardinality. Drag the round grips to choose another
            attachment.
          </p>
          {selectedRoute.warnings.map((w) => (
            <p className="dg-canvas-layout-warning" key={w}>
              {w}
            </p>
          ))}
          <div className="dg-canvas-panel-actions">
            <button
              onClick={() =>
                onOpenRelationship(selectedRoute.relationship.relationshipId)
              }
            >
              Edit properties
            </button>
            <button
              disabled={readOnly || !!preview}
              onClick={() =>
                resetRoute(selectedRoute.relationship.relationshipId)
              }
            >
              <RotateCcw size={13} />
              Reset route
            </button>
          </div>
        </div>
      )}
      {entityPreview && <EntityPreview document={document} perspective={diagram?.perspective} {...entityPreview} preferences={preferences} onClose={closeEntityPreview} onEnter={keepEntityPreview} onLeave={leaveEntity}/> }
      {menu && (
        <div
          ref={contextMenuElement}
          className="dg-canvas-context"
          role="menu"
          style={{ left: menu.x, top: menu.y, fontSize: contextMenuFontSize }}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault(); e.stopPropagation(); setMenu(null);
              container.current?.focus({ preventScroll: true });
              return;
            }
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key))
              return;
            e.preventDefault();
            e.stopPropagation();
            const items = Array.from(
              e.currentTarget.querySelectorAll<HTMLButtonElement>(
                "button:not(:disabled)",
              ),
            );
            const current = items.indexOf(
              window.document.activeElement as HTMLButtonElement,
            );
            const next =
              e.key === "Home"
                ? 0
                : e.key === "End"
                  ? items.length - 1
                  : (current +
                      (e.key === "ArrowDown" ? 1 : -1) +
                      items.length) %
                    items.length;
            items[next]?.focus();
          }}
        >
          <div className="dg-canvas-context-title">
            {menu.kind === "node"
              ? objects.get(menu.fieldId ?? selectedNode?.objectId ?? "")?.name
              : menu.kind === "edge"
                ? relationships.find((r) => r.relationshipId === menu.id)?.name
                : menu.kind === "selection" ? `${selection.length} selected objects` : "Canvas"}
          </div>
          {menu.kind === "canvas" && onAddEntity && <button role="menuitem" disabled={!canEditModel||readOnly||!!preview} onClick={()=>{onAddEntity(menu.point);setMenu(null);}}><Plus size={14}/>{terms.physical?"Create table":"Create entity"}</button>}
          {(menu.kind === "canvas" || menu.kind === "selection" || menu.kind === "node") && <>
            {onCreateSubjectArea && <button role="menuitem" disabled={!canEditModel||!!preview} onClick={()=>{onCreateSubjectArea(selection);setMenu(null);}}><Layers size={14}/>Create subject area</button>}
            {onCreateView && <button role="menuitem" disabled={!canEditModel||!!preview} onClick={()=>{onCreateView(selection);setMenu(null);}}><Eye size={14}/>Create view</button>}
            {diagram.role!=="main" && onAddEntities && <button role="menuitem" disabled={!canEditModel||readOnly||!!preview} onClick={()=>{onAddEntities();setMenu(null);}}><Plus size={14}/>Add entities</button>}
          </>}
          <div className="dg-canvas-copy-menu">
            <button role="menuitem" disabled={exportBusy || !!preview || !exportRender} onClick={() => void exportDiagram("svg", true)}><Copy size={14}/>Copy as SVG</button>
            <button role="menuitem" disabled={exportBusy || !!preview || !exportRender} onClick={() => void exportDiagram("png", true)}><Copy size={14}/>Copy as PNG</button>
            <button role="menuitem" disabled={exportBusy || !!preview || !exportRender} onClick={() => void exportDiagram("svg", false)}><Download size={14}/>Download SVG</button>
            <button role="menuitem" disabled={exportBusy || !!preview || !exportRender} onClick={() => void exportDiagram("png", false)}><Download size={14}/>Download PNG</button>
          </div>
          <div className="dg-canvas-menu-divider"/>
          {menu.kind === "node" && selectedNode && (
            <>
              <button
                role="menuitem"
                onClick={() => {
                  onOpenObject(menu.fieldId ?? selectedNode.objectId);
                  setMenu(null);
                }}
              >
                <Table2 size={14} />
                Properties
              </button>
              {onAddAttribute && <button role="menuitem" disabled={!canEditModel || !!preview} onClick={() => { onAddAttribute(selectedNode.objectId, menu.fieldId); setMenu(null); }}><Plus size={14} />{`Add ${terms.field}`}</button>}
              <button role="menuitem" onClick={()=>{const bounds=container.current?.getBoundingClientRect();setEntityPreview({objectId:selectedNode.objectId,x:menu.x+(bounds?.left??0),y:menu.y+(bounds?.top??0),mode:'click'});setMenu(null);}}><Eye size={14}/>Preview {terms.object}</button>
              {onBeginRelationship && <button role="menuitem" disabled={!canEditModel || !!preview} onClick={()=>{onBeginRelationship(selectedNode.objectId);setMenu(null);}}><RelationshipIcon size={14}/>Create relationship</button>}
              <button role="menuitem" disabled={readOnly || !!preview} onClick={()=>{const b=baseBoxes.find(b=>b.id===selectedNode.appearanceId)!;const locked=selectedNode.sizeLocked??(selectedNode.width!==undefined||selectedNode.height!==undefined);commit([{type:'appearance.update',diagramId:diagram.diagramId,appearanceId:selectedNode.appearanceId,changes:{sizeLocked:!locked,...(!locked?{width:b.width,height:b.height}:{})}}],locked?'Unlock entity size':'Lock entity size');setMenu(null);}}><LockKeyhole size={14}/>{(selectedNode.sizeLocked??(selectedNode.width!==undefined||selectedNode.height!==undefined))?'Unlock size':'Lock size'}</button>
              <button role="menuitem" disabled={readOnly || !!preview || (selectedNode.sizeLocked ?? (selectedNode.width !== undefined || selectedNode.height !== undefined))} onClick={()=>{const object=objects.get(selectedNode.objectId)!;const children=entityFields(document,object.objectId);const dimensions=entitySize(object,children.fields,children.keys,{...selectedNode,width:undefined,height:undefined,sizeLocked:false},preferences,conceptual,detailLevel,undefined,true,terms.physical,entityDisplay);localSizes.current.delete(selectedNode.appearanceId);commit([{type:'appearance.update',diagramId:diagram.diagramId,appearanceId:selectedNode.appearanceId,changes:{sizeLocked:false,width:dimensions.width,height:dimensions.height}}], 'Fit entity size to content');setMenu(null);}}><Maximize2 size={14}/>Fit size to content</button>
              {onOpenAppearance && <button role="menuitem" disabled={readOnly || !!preview} onClick={() => { onOpenAppearance({ kind: "entity", id: selectedNode.appearanceId }); setMenu(null); }}><Palette size={14} />Appearance</button>}
              <button
                role="menuitem"
                disabled={readOnly || !!preview}
                onClick={() => {
                  commit(
                    [
                      {
                        type: "appearance.update",
                        diagramId: diagram.diagramId,
                        appearanceId: selectedNode.appearanceId,
                        changes: { pinned: !selectedNode.pinned },
                      },
                    ],
                    selectedNode.pinned
                      ? "Unpin entity position"
                      : "Pin entity position",
                  );
                  setMenu(null);
                }}
              >
                {selectedNode.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                {selectedNode.pinned ? "Unpin position" : "Pin position"}
              </button>
              <button role="menuitem" onClick={() => { setDetailOpen(true); setMenu(null); }}><Layers size={14} />{terms.physical ? "Table" : "Entity"} detail for this diagram</button>
              <div className="dg-canvas-menu-divider" />
              <button
                role="menuitem"
                disabled={readOnly || !!preview || selectedNode.pinned}
                onClick={() => {
                  commit(
                    [
                      {
                        type: "appearance.update",
                        diagramId: diagram.diagramId,
                        appearanceId: selectedNode.appearanceId,
                        changes: {
                          position: {
                            x: selectedNode.position.x + 24,
                            y: selectedNode.position.y,
                          },
                        },
                      },
                    ],
                    "Move entity right",
                  );
                  setMenu(null);
                }}
              >
                <Move size={14} />
                Move right 24 px
              </button>
              {diagram.role!=="main" && <button
                role="menuitem"
                disabled={readOnly || !!preview}
                onClick={() => {
                  commit(
                    [
                      {
                        type: "appearance.delete",
                        diagramId: diagram.diagramId,
                        appearanceId: selectedNode.appearanceId,
                      },
                    ],
                    "Remove entity from this view",
                  );
                  setMenu(null);
                }}
              >
                <Eye size={14} />
                Remove from this view
              </button>}
            </>
          )}
          {menu.kind === "edge" && menu.id && (
            <>
              <button
                role="menuitem"
                onClick={() => {
                  onOpenRelationship(menu.id!);
                  setMenu(null);
                }}
              >
                <GitBranch size={14} />
                Edit relationship
              </button>
              <button role="menuitem" onClick={() => { setRouteInfo(true); setMenu(null); }}><SlidersHorizontal size={14} />Line and label appearance</button>
              {onOpenAppearance && <button role="menuitem" disabled={readOnly || !!preview} onClick={() => { onOpenAppearance({ kind: "relationship", id: menu.id }); setMenu(null); }}><Palette size={14} />Appearance</button>}
              <button
                role="menuitem"
                disabled={readOnly || !!preview}
                onClick={() => {
                  const c = connectors.find(
                    (c) => c.relationshipId === menu.id,
                  ) ?? { relationshipId: menu.id! };
                  const route = routes.find(item => item.relationship.relationshipId === menu.id);
                  if (!route) return;
                  const waypoints = insertWaypoint(route.points, c.waypoints ?? [], menu.point);
                  changeConnector(menu.id!, { ...c, ...manualConnectorChanges(route, waypoints) }, true);
                  setMenu(null);
                }}
              >
                <Plus size={14} />
                Add waypoint here
              </button>
              {menu.waypoint !== undefined && (
                <button
                  role="menuitem"
                  disabled={readOnly || !!preview}
                  onClick={() => {
                    const c = connectors.find(
                      (c) => c.relationshipId === menu.id,
                    )!;
                    changeConnector(
                      menu.id!,
                      {
                        ...c,
                        waypoints: c.waypoints?.filter(
                          (_, i) => i !== menu.waypoint,
                        ),
                      },
                      true,
                    );
                    setMenu(null);
                  }}
                >
                  <Minus size={14} />
                  Remove waypoint
                </button>
              )}
              <button
                role="menuitem"
                disabled={readOnly || !!preview}
                onClick={() => resetRoute(menu.id!)}
              >
                <RotateCcw size={14} />
                Reset automatic route
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  setRouteInfo(true);
                  setMenu(null);
                }}
              >
                <Info size={14} />
                Why this route?
              </button>
            </>
          )}
          {menu.kind === "canvas" && (
            <>
              <button role="menuitem" disabled={readOnly || !!preview} onClick={fitModelToText}><Maximize2 size={14}/>Fit all entities to text</button>
              {onOpenAppearance && <button role="menuitem" disabled={readOnly || !!preview} onClick={() => { onOpenAppearance({ kind: "diagram", id: diagram.diagramId }); setMenu(null); }}><Palette size={14} />Diagram appearance</button>}
              <button
                role="menuitem"
                onClick={() => {
                  void fitDiagram(0);
                  setMenu(null);
                }}
              >
                <Maximize2 size={14} />
                Fit model
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  onSelection(allDiagramIds);
                  setMenu(null);
                }}
              >
                <MousePointer2 size={14} />
                Select all visible
              </button>
              <button
                role="menuitem"
                disabled={readOnly || !!preview}
                onClick={() => {
                  runLayout();
                  setMenu(null);
                }}
              >
                <Network size={14} />
                Preview automatic layout
              </button>
            </>
          )}
          <div className="dg-canvas-menu-divider" />
          <button
            role="menuitem"
            onClick={() => {
              onHelp(menu.kind === "edge" ? "relationships" : "diagrams");
              setMenu(null);
            }}
          >
            <HelpCircle size={14} />
            Help for this{" "}
            {menu.kind === "edge"
              ? "relationship"
              : menu.kind === "node"
                ? terms.object
                : "canvas"}
          </button>
        </div>
      )}
    </div>
  );
}
export default function ModelCanvas(props: ModelCanvasProps) {
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}
